import {
  TILE,
  MAP_SCALE,
} from "../constants.js";
import { WORLD_STATE, saveWorldStateToStorage } from "../data/worldState.js";
import { Player } from "../entities/Player.js";
import { Enemy } from "../entities/Enemy.js";
import { Npc } from "../entities/Npc.js";
import { ENEMY_TYPES } from "../data/enemies.js";
import { NPC_TYPES, resolveNpcTypeDef } from "../data/npcs.js";
import { Projectile } from "../entities/Projectile.js";
import { resolveWebSocketUrl } from "../net/networkConfig.js";
import { createNetworkClient, stopNetworkClient, NETWORK_ENABLED } from "../net/networkBootstrap.js";
import { readSessionId, writeSessionId, readPlayerName, readSelectedCharacter, isValidSessionId } from "../net/networkSession.js";
import { applyNetworkState as applyNetworkStateSync } from "../net/networkStateSync.js";
import { handleNetworkProjectileSpawn, handleNetworkProjectileHit } from "../net/networkProjectiles.js";
import { handleNetworkCombatHit, handleNetworkCombatSwing } from "../net/networkCombatSync.js";
import { sendLocalPlayerInput as sendLocalPlayerInputSync, updateNetworkRuntime } from "../net/networkLocalPlayerSync.js";
import { createHUDController } from "./features/ui/hud.js";
import { applyUIStateTransitions } from "./features/ui/state.js";
import { applyCombatActions, applyCombatWorldUpdates, applyEnemyCombat } from "./features/combat/actions.js";
import { applyPlayerMovement } from "./features/movement/playerMovement.js";
import { applyFramePostUpdate } from "./features/frame/update.js";
import { MAP_DEFS } from "../data/maps.js";
import {
  getObjectProperty,
  parsePortalZones,
  resolveSpawnPosition,
  spawnCollectible,
  spawnRandomTestCollectibles,
} from "./features/map/sceneSetup.js";
import {
  createRemotePlayer as createRemotePlayerForScene,
  removeRemotePlayer as removeRemotePlayerForScene,
  updateRemotePlayers as updateRemotePlayersForScene,
} from "./features/networking/remotePlayers.js";
import { CHARACTER_CATALOG, CHARACTER_ID_ALIASES, DEFAULT_CHARACTER_ID } from "../../../shared/characterCatalog.js";

const CHARACTER_OPTIONS = CHARACTER_CATALOG;

const BASE_ANIM_SPECS = [
  { state: "Idle", suffix: "idle", end: 11, repeat: -1 },
  { state: "Idle Blinking", suffix: "idle_blinking", end: 11, repeat: -1 },
  { state: "Walking", suffix: "walking", end: 15, repeat: -1 },
  { state: "Running", suffix: "running", end: 7, repeat: -1 },
  { state: "Jump Loop", suffix: "jump_loop", end: 3, repeat: -1 },
  { state: "Sliding", suffix: "sliding", end: 3, repeat: -1 },
  { state: "Slashing", suffix: "slashing", end: 7, repeat: 0 },
  { state: "Run Slashing", suffix: "run_slashing", end: 7, repeat: 0 },
  { state: "Slashing in The Air", suffix: "slashing_in_the_air", end: 7, repeat: 0 },
  { state: "Throwing", suffix: "throwing", end: 7, repeat: 0 },
  { state: "Run Throwing", suffix: "run_throwing", end: 7, repeat: 0 },
  { state: "Throwing in The Air", suffix: "throwing_in_the_air", end: 7, repeat: 0 },
  { state: "Kicking", suffix: "kicking", end: 7, repeat: 0 },
  { state: "Hurt", suffix: "hurt", end: 7, repeat: 0 },
  { state: "Dying", suffix: "dying", end: 9, repeat: 0 },
];
const CHARACTER_MISSING_STATES = {
  ghost_knight_1: new Set(["Idle Blinking"]),
  spartan_knight: new Set(["Idle Blinking"]),
};

const COLLECTIBLE_TYPE_TO_TEXTURE = {
  apple: "collectible_apple",
  heart: "collectible_heart",
  meat: "collectible_meat",
  collectible_apple: "collectible_apple",
  collectible_heart: "collectible_heart",
  collectible_meat: "collectible_meat",
};
const COLLECTIBLE_TEXTURE_KEYS = ["collectible_apple", "collectible_heart", "collectible_meat"];
const LEGACY_ENEMY_TYPE_KEYS = new Set([
  "archer_1",
  "archer_2",
  "archer_3",
  "archer_guy",
  "assassin_guy",
  "black_ninja",
  "medieval_mage",
  "white_ninja",
]);
const TEMP_RANDOM_ITEM_COUNT = 12;
const PLATFORM_ENEMY_REINFORCEMENT_SPACING_PX = 120;
const PLATFORM_ENEMY_REINFORCEMENT_MAX_PER_ANCHOR = 3;
const PLATFORM_ENEMY_SPAWN_MIN_GAP_PX = 90;
const BASE_FRAME_MS = 1000 / 60;
const SIM_STEP_MS = 1000 / 60;
const MAX_CATCHUP_STEPS = 5;
const MAX_ACCUMULATED_MS = SIM_STEP_MS * MAX_CATCHUP_STEPS;
const MAX_FRAME_DT_MS = 250;
const MIN_PHYSICS_FRAME_SCALE = 0.5;
const MAX_PHYSICS_FRAME_SCALE = 12;
const MAX_NORMALIZED_DT_MS = 250;
const DEBUG_DELTA_TELEMETRY = false;
const MAX_GROUNDED_STEP_UP = 6;
const ENABLE_RUNTIME_HITBOX_TOGGLE = true;
const FEET_UI_OFFSET_Y = 6;
const CHAT_MAX_HISTORY = 8;
const CHAT_MAX_TEXT_LENGTH = 140;
const CHAT_BUBBLE_TTL_MS = 4200;
const CHAT_BUBBLE_MAX_WIDTH = 240;
const CHAT_BUBBLE_PADDING_X = 8;
const CHAT_BUBBLE_PADDING_Y = 6;
const CHAT_BUBBLE_ANCHOR_OFFSET_Y = 120;
const CHAT_BUBBLE_DEPTH = 3600;
const SCOREBOARD_OFFLINE_HISTORY_KEY = "hene.town.scoreboard.offline.v1";
const SCOREBOARD_OFFLINE_HISTORY_MAX = 300;
const SCOREBOARD_MAX_ROWS = 10;
const SCOREBOARD_UNKNOWN_NAME = "Player";
const SCOREBOARD_UNKNOWN_ID = "unknown";
const SCOREBOARD_SERVER_RESET_VERSION_KEY_PREFIX = "hene.town.scoreboard.serverResetVersion.v1";
const SCOREBOARD_FETCH_MIN_INTERVAL_MS = 5000;
const QUEST_BOARD_FRAME_NAME = "quest_board";
const BULLETIN_BOARD_TEXT_MAX = 280;
const QUEST_BOARD_HINT_TEXT = "Leave a message";
const QUEST_BOARD_HINT_BOB_PX = 6;
const QUEST_BOARD_HINT_DEPTH = 3200;

function sanitizeScoreboardName(rawName) {
  if (typeof rawName !== "string") return SCOREBOARD_UNKNOWN_NAME;
  const trimmed = rawName.trim();
  return trimmed.length > 0 ? trimmed.slice(0, 24) : SCOREBOARD_UNKNOWN_NAME;
}

function normalizeScore(statsValue) {
  const value = Number(statsValue);
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.floor(value));
}

function normalizeBulletinBoardMessages(messages) {
  if (!Array.isArray(messages)) return [];
  const normalized = [];
  for (const entry of messages) {
    if (!entry || typeof entry !== "object") continue;
    const id = typeof entry.id === "string" ? entry.id.trim().toLowerCase() : "";
    const playerId = typeof entry.playerId === "string" ? entry.playerId.trim() : "";
    const name = sanitizeScoreboardName(entry.name);
    let text = typeof entry.text === "string" ? entry.text.replace(/\r\n?/g, "\n").trim() : "";
    if (!id || !playerId || !text) continue;
    if (text.length > BULLETIN_BOARD_TEXT_MAX) text = text.slice(0, BULLETIN_BOARD_TEXT_MAX).trim();
    const createdAt = Number.isFinite(Number(entry.createdAt)) ? Number(entry.createdAt) : Date.now();
    const updatedAt = Number.isFinite(Number(entry.updatedAt)) ? Number(entry.updatedAt) : createdAt;
    normalized.push({
      id,
      playerId,
      name,
      text,
      createdAt,
      updatedAt,
    });
  }
  normalized.sort((a, b) => {
    if (a.createdAt !== b.createdAt) return a.createdAt - b.createdAt;
    return a.id.localeCompare(b.id);
  });
  return normalized;
}

function normalizeScoreboardRecord(rawRecord) {
  const playerId = typeof rawRecord?.id === "string" ? rawRecord.id.trim() : null;
  if (!playerId) return null;
  const now = Number.isFinite(rawRecord?.lastSeenAt) ? Number(rawRecord.lastSeenAt) : Date.now();
  return {
    id: playerId,
    name: sanitizeScoreboardName(rawRecord?.name),
    playerKills: normalizeScore(rawRecord?.playerKills),
    playerDeaths: normalizeScore(rawRecord?.playerDeaths),
    enemyKills: normalizeScore(rawRecord?.enemyKills),
    enemyDeaths: normalizeScore(rawRecord?.enemyDeaths),
    lastSeenAt: now,
  };
}

function normalizeAnyScoreboardRecord(rawRecord) {
  if (!rawRecord || typeof rawRecord !== "object") return null;
  const stats = rawRecord?.stats && typeof rawRecord.stats === "object" ? rawRecord.stats : rawRecord;
  return normalizeScoreboardRecord({
    id: rawRecord.id,
    name: rawRecord.name,
    playerKills: stats.playerKills,
    playerDeaths: stats.playerDeaths,
    enemyKills: stats.enemyKills,
    enemyDeaths: stats.enemyDeaths,
    lastSeenAt: rawRecord.lastSeenAt,
  });
}

function mergeScoreboardRecord(existing, incoming) {
  if (!existing) return { ...incoming };
  const existingSeenAt = Number.isFinite(existing.lastSeenAt) ? Number(existing.lastSeenAt) : 0;
  const incomingSeenAt = Number.isFinite(incoming.lastSeenAt) ? Number(incoming.lastSeenAt) : 0;
  return {
    id: incoming.id,
    name: incomingSeenAt >= existingSeenAt
      ? sanitizeScoreboardName(incoming.name)
      : sanitizeScoreboardName(existing.name),
    playerKills: Math.max(normalizeScore(existing.playerKills), normalizeScore(incoming.playerKills)),
    playerDeaths: Math.max(normalizeScore(existing.playerDeaths), normalizeScore(incoming.playerDeaths)),
    enemyKills: Math.max(normalizeScore(existing.enemyKills), normalizeScore(incoming.enemyKills)),
    enemyDeaths: Math.max(normalizeScore(existing.enemyDeaths), normalizeScore(incoming.enemyDeaths)),
    lastSeenAt: Math.max(existingSeenAt, incomingSeenAt, Date.now()),
  };
}

function resolveScoreboardApiUrlFromWs(wsUrl) {
  if (typeof wsUrl !== "string" || !wsUrl.trim()) return null;
  try {
    const url = new URL(wsUrl);
    if (url.protocol === "ws:") {
      url.protocol = "http:";
    } else if (url.protocol === "wss:") {
      url.protocol = "https:";
    } else {
      return null;
    }
    url.pathname = "/api/leaderboard/all-time";
    url.search = "";
    url.hash = "";
    return url.toString();
  } catch {
    return null;
  }
}

function loadScoreboardOfflineHistory() {
  try {
    const raw = globalThis?.localStorage?.getItem?.(SCOREBOARD_OFFLINE_HISTORY_KEY);
    if (!raw) return new Map();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Map();

    const history = new Map();
    for (const entry of parsed) {
      const normalized = normalizeScoreboardRecord(entry);
      if (!normalized) continue;
      if (!history.has(normalized.id)) {
        history.set(normalized.id, normalized);
      }
    }
    return history;
  } catch {
    return new Map();
  }
}

function saveScoreboardOfflineHistory(historyById) {
  try {
    const payload = Array.from(historyById?.entries?.() || []).map(([, value]) => ({
      id: value.id,
      name: value.name,
      playerKills: value.playerKills,
      playerDeaths: value.playerDeaths,
      enemyKills: value.enemyKills,
      enemyDeaths: value.enemyDeaths,
      lastSeenAt: value.lastSeenAt || Date.now(),
    }));
    payload.sort((a, b) => (b.lastSeenAt || 0) - (a.lastSeenAt || 0));
    const trimmed = payload.slice(0, SCOREBOARD_OFFLINE_HISTORY_MAX);
    globalThis?.localStorage?.setItem?.(SCOREBOARD_OFFLINE_HISTORY_KEY, JSON.stringify(trimmed));
  } catch {
    // no-op
  }
}

function makeScoreboardResetVersionStorageKey(apiUrl) {
  const safeApiUrl = typeof apiUrl === "string" ? apiUrl.trim() : "";
  if (!safeApiUrl) return SCOREBOARD_SERVER_RESET_VERSION_KEY_PREFIX;
  return `${SCOREBOARD_SERVER_RESET_VERSION_KEY_PREFIX}:${safeApiUrl}`;
}

function readScoreboardServerResetVersion(apiUrl) {
  try {
    const key = makeScoreboardResetVersionStorageKey(apiUrl);
    const raw = globalThis?.localStorage?.getItem?.(key);
    const value = Number(raw);
    if (!Number.isFinite(value)) return 0;
    return Math.max(0, Math.floor(value));
  } catch {
    return 0;
  }
}

function writeScoreboardServerResetVersion(apiUrl, resetVersion) {
  try {
    const key = makeScoreboardResetVersionStorageKey(apiUrl);
    const safeVersion = Number.isFinite(Number(resetVersion))
      ? Math.max(0, Math.floor(Number(resetVersion)))
      : 0;
    globalThis?.localStorage?.setItem?.(key, String(safeVersion));
  } catch {
    // no-op
  }
}

function applyScoreboardDisambiguation(rows) {
  const normalized = Array.isArray(rows) ? rows : [];
  const counts = new Map();
  const shortenIdForDisplay = (id) => {
    const raw = String(id || SCOREBOARD_UNKNOWN_ID).trim();
    if (!raw) return `${SCOREBOARD_UNKNOWN_ID}...`;
    const firstSegment = raw.split("-")[0] || raw;
    const safeSegment = firstSegment.slice(0, 12);
    return `${safeSegment}...`;
  };
  for (const row of normalized) {
    const baseName = sanitizeScoreboardName(row?.name);
    counts.set(baseName, (counts.get(baseName) || 0) + 1);
  }
  return normalized.map((row) => {
    const baseName = sanitizeScoreboardName(row?.name);
    const id = String(row?.id ?? SCOREBOARD_UNKNOWN_ID);
    const needsDisambiguation = counts.get(baseName) > 1;
    return {
      ...row,
      id,
      name: baseName,
      displayName: needsDisambiguation ? `${baseName} (${shortenIdForDisplay(id)})` : baseName,
      playerKills: normalizeScore(row?.playerKills),
      playerDeaths: normalizeScore(row?.playerDeaths),
      enemyKills: normalizeScore(row?.enemyKills),
      enemyDeaths: normalizeScore(row?.enemyDeaths),
    };
  });
}

function getCharacterDef(characterId) {
  const raw = typeof characterId === "string" ? characterId.trim().toLowerCase() : "";
  const resolved = CHARACTER_ID_ALIASES[raw] || raw;
  return CHARACTER_OPTIONS.find((c) => c.id === resolved) || CHARACTER_OPTIONS.find((c) => c.id === DEFAULT_CHARACTER_ID) || CHARACTER_OPTIONS[0];
}

function composeAtlasKey(prefix, suffix) {
  return `${prefix}_${suffix}`;
}

function composeAnimKey(characterId, state) {
  return `char:${characterId}:${state}`;
}

function characterSupportsState(character, state) {
  const missing = CHARACTER_MISSING_STATES[character?.id];
  return !missing || !missing.has(state);
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function normalizeDeltaMs(rawDelta) {
  if (!Number.isFinite(rawDelta) || rawDelta <= 0) return BASE_FRAME_MS;
  const ms = rawDelta < 4 ? rawDelta * 1000 : rawDelta;
  return clamp(ms, 1, MAX_NORMALIZED_DT_MS);
}

function isSpawnInWorldBounds(pos, worldW, worldH, entityW = 28, entityH = 40, margin = 15, bottomTolerance = 8) {
  if (!pos || !Number.isFinite(pos.x) || !Number.isFinite(pos.y)) return false;
  if (pos.x < margin) return false;
  if (pos.x + entityW > worldW - margin) return false;
  if (pos.y < 0) return false;
  if (pos.y + entityH > worldH + bottomTolerance) return false;
  return true;
}

// ── SAT Collision Helpers ────────────────────────────────────────────────────

function aabbVertices(x, y, w, h) {
  return [
    { x, y },
    { x: x + w, y },
    { x: x + w, y: y + h },
    { x, y: y + h },
  ];
}

function projectOntoAxis(verts, axis) {
  let min = Infinity, max = -Infinity;
  for (const v of verts) {
    const dot = v.x * axis.x + v.y * axis.y;
    if (dot < min) min = dot;
    if (dot > max) max = dot;
  }
  return { min, max };
}

function getPolyAxes(verts) {
  const axes = [];
  for (let i = 0; i < verts.length; i++) {
    const a = verts[i];
    const b = verts[(i + 1) % verts.length];
    const edgeX = b.x - a.x;
    const edgeY = b.y - a.y;
    const len = Math.sqrt(edgeX * edgeX + edgeY * edgeY);
    if (len === 0) continue;
    axes.push({ x: -edgeY / len, y: edgeX / len });
  }
  return axes;
}

function satOverlap(aabbVerts, polyVerts) {
  const aabbAxes = [{ x: 1, y: 0 }, { x: 0, y: 1 }];
  const polyAxes = getPolyAxes(polyVerts);
  const allAxes = aabbAxes.concat(polyAxes);

  let minOverlap = Infinity;
  let minAxis = null;

  for (const axis of allAxes) {
    const projA = projectOntoAxis(aabbVerts, axis);
    const projB = projectOntoAxis(polyVerts, axis);
    const overlap = Math.min(projA.max - projB.min, projB.max - projA.min);
    if (overlap <= 0) return null;
    if (overlap < minOverlap) {
      minOverlap = overlap;
      minAxis = axis;
    }
  }

  const aabbCx = (aabbVerts[0].x + aabbVerts[2].x) / 2;
  const aabbCy = (aabbVerts[0].y + aabbVerts[2].y) / 2;
  let polyCx = 0, polyCy = 0;
  for (const v of polyVerts) { polyCx += v.x; polyCy += v.y; }
  polyCx /= polyVerts.length;
  polyCy /= polyVerts.length;

  const dx = aabbCx - polyCx;
  const dy = aabbCy - polyCy;
  if (dx * minAxis.x + dy * minAxis.y < 0) {
    minAxis = { x: -minAxis.x, y: -minAxis.y };
  }

  return { overlap: minOverlap, axis: minAxis };
}

function pointInPolygon(px, py, verts) {
  let inside = false;
  for (let i = 0, j = verts.length - 1; i < verts.length; j = i++) {
    const xi = verts[i].x, yi = verts[i].y;
    const xj = verts[j].x, yj = verts[j].y;
    if ((yi > py) !== (yj > py) && px < (xj - xi) * (py - yi) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

function aabbOverlapsBounds(ax, ay, aw, ah, bounds) {
  return ax + aw > bounds.minX && ax < bounds.maxX &&
         ay + ah > bounds.minY && ay < bounds.maxY;
}

function aabbOverlap(a, b) {
  return a.x < b.x + b.w && a.x + a.w > b.x &&
         a.y < b.y + b.h && a.y + a.h > b.y;
}

function asLayerNameList(names) {
  return Array.isArray(names) ? names : [names];
}

const FLIP_H_FLAG = 0x80000000;
const FLIP_V_FLAG = 0x40000000;
const FLIP_D_FLAG = 0x20000000;
const GID_MASK = ~(FLIP_H_FLAG | FLIP_V_FLAG | FLIP_D_FLAG);

function parseTiledGid(rawGid) {
  const gid = rawGid & GID_MASK;
  return {
    gid,
    flipX: !!(rawGid & FLIP_H_FLAG),
    flipY: !!(rawGid & FLIP_V_FLAG),
    flipD: !!(rawGid & FLIP_D_FLAG),
  };
}

// ── Scene ────────────────────────────────────────────────────────────────────
export class BaseMapScene extends Phaser.Scene {
  constructor(sceneKey, mapId) {
    super(sceneKey);
    this.mapId = mapId;
    this.mapConfig = MAP_DEFS[mapId];
    if (!this.mapConfig) {
      throw new Error(`Missing map config for mapId: ${mapId}`);
    }
    this.portalCooldownMs = 0;
    this.portalZones = [];
    this.questBoardSprites = [];
    this.questBoardHints = [];
    this.lastResumeSaveAt = 0;
    this.lastDeltaTelemetryAt = 0;
    this.simAccumulatorMs = 0;
    this.simLastTimeMs = 0;
    this.lastSimStepCount = 0;
    this.lastPlayerAttackHitbox = null;
    this.renderLerpAlpha = 0;
    this.renderPrevPlayerX = 0;
    this.renderPrevPlayerY = 0;
    this.renderCurrPlayerX = 0;
    this.renderCurrPlayerY = 0;
    this.frameScale = 1;
    this.renderFrameScale = 1;
    this.spawnUsedFallbackFromInvalidResume = false;
    this.netClient = null;
    this.netStatus = "offline";
    this.netPlayerId = null;
    this.netSessionId = null;
    this.netServerTickRate = 30;
    this.netLifeState = "unknown";
    this.netInvulnerableUntil = 0;
    this.netServerTimeOffsetMs = null;
    this.netInputSeq = 0;
    this.netLastInputSentAt = 0;
    this.netInputAccumulatorMs = 0;
    this.netLastInputSchedulerAtMs = 0;
    this.netInputSendIntervalsMs = [];
    this.netInputSendStats = null;
    this.netInputFlags = {
      left: false,
      right: false,
      up: false,
      down: false,
      jump: false,
      dash: false,
      slash: false,
      throw: false,
      kick: false,
      respawn: false,
    };
    this.remotePlayers = new Map();
    this.netProjectilesById = new Map();
    this.netPendingProjectilesByToken = new Map();
    this.netStatusText = null;
    this.netWsUrl = null;
    this.scoreboardApiUrl = null;
    this.ui = null;
    this.netRemoteMotionStats = null;
    this.netRemoteSnapEventsMs = [];
    this.localCharacterId = DEFAULT_CHARACTER_ID;
    this.localPlayerNameText = null;
    this.characterFootOffsets = new Map();
    this.characterCenterOffsets = new Map();
    this.characterMeasuredOrigins = new Map();
    this.footOffsetCanvas = null;
    this.footOffsetCtx = null;
    this.uiInputLockedLast = false;
    this.prevRespawnKeyDown = false;
    this.remotePlayerLabelOffsetY = FEET_UI_OFFSET_Y;
    this.loadingState = globalThis.HENE_LOADING_STATE || null;
    this.chatOpen = false;
    this.chatInputText = "";
    this.chatHistory = [];
    this.chatMaxHistory = CHAT_MAX_HISTORY;
    this.chatCollapsed = false;
    this.chatInputHandler = null;
    this.bulletinBoardMessages = [];
    this.bulletinBoardOpen = false;
    this.bulletinBoardModalController = null;
    this.scoreboardRows = [];
    this.scoreboardOfflineRows = [];
    this.scoreboardDisplayNameById = new Map();
    this.scoreboardKnownPlayers = loadScoreboardOfflineHistory();
    this.scoreboardServerFetchInFlight = false;
    this.scoreboardServerLoaded = false;
    this.scoreboardServerResetVersion = 0;
    this.scoreboardLastFetchedAt = 0;
    this.netStartupPlayersReady = false;
    this.netStartupScoreboardReady = false;
    this.netStartupPhaseLabel = "";
    this.localPlayerStats = null;
    this.keyEnter = null;
    this.keyEsc = null;
    this.keyF3 = null;
    this.chatBubbles = new Map();
    this.chatBubbleTextConfig = {
      fontFamily: "monospace",
      fontSize: "12px",
      color: "#f8fafc",
      stroke: "#000000",
      strokeThickness: 2,
      align: "left",
    };
  }

  preload() {
    const cacheBust = "?" + Date.now();
    const selectedCharacterId = this.getSelectedCharacterId();
    this.localCharacterId = selectedCharacterId;
    if (this.loadingState && !this.loadingState.isComplete?.()) {
      this.loadingState.setMessage("Loading scene assets...");
      const onProgress = (value) => {
        this.loadingState.setProgress(value);
      };
      const onComplete = () => {
        this.loadingState.setProgress(1);
        this.loadingState.setMessage("Starting scene...");
      };
      this.load.on("progress", onProgress);
      this.load.once("complete", onComplete);
    }

    for (const character of CHARACTER_OPTIONS) {
      for (const spec of BASE_ANIM_SPECS) {
        if (!characterSupportsState(character, spec.state)) continue;
        const atlas = composeAtlasKey(character.prefix, spec.suffix);
        this.load.atlas(atlas, character.dir + atlas + ".png" + cacheBust, character.dir + atlas + ".json" + cacheBust);
      }
    }

    // Enemy spritesheets (all types)
    const loadedPrefixes = new Set();
    for (const typeDef of Object.values(ENEMY_TYPES)) {
      loadedPrefixes.add(typeDef.prefix);
      for (const anim of typeDef.anims) {
        const key = typeDef.prefix + "_" + anim.suffix;
        this.load.atlas(key, typeDef.dir + key + ".png" + cacheBust, typeDef.dir + key + ".json" + cacheBust);
      }
    }

    // NPC-only character spritesheets
    for (const typeDef of Object.values(NPC_TYPES)) {
      if (loadedPrefixes.has(typeDef.prefix)) continue;
      for (const anim of typeDef.anims) {
        const key = typeDef.prefix + "_" + anim.suffix;
        this.load.atlas(key, typeDef.dir + key + ".png" + cacheBust, typeDef.dir + key + ".json" + cacheBust);
      }
    }

    this.load.atlas("platformer", "assets/tilesets/village/platformer_atlas.png" + cacheBust, "assets/tilesets/village/platformer_atlas.json" + cacheBust);
    this.load.atlas("building", "assets/tilesets/village/building_atlas.png" + cacheBust, "assets/tilesets/village/building_atlas.json" + cacheBust);
    this.load.atlas("environment", "assets/tilesets/village/environment_atlas.png" + cacheBust, "assets/tilesets/village/environment_atlas.json" + cacheBust);

    this.load.atlas("food", "assets/items/food/food_atlas.png" + cacheBust, "assets/items/food/food_atlas.json" + cacheBust);
    this.load.image("collectible_apple", "assets/items/collectibles/apple.png" + cacheBust);
    this.load.image("collectible_heart", "assets/items/collectibles/heart.png" + cacheBust);
    this.load.image("collectible_meat", "assets/items/collectibles/meat.png" + cacheBust);
    this.load.image("collectible_bubble", "assets/items/collectibles/object_bubble.png" + cacheBust);
    for (let i = 1; i <= 9; i++) {
      this.load.image(`explosion_${i}`, `assets/effects/explosions/9/${i}.png` + cacheBust);
    }

    this.load.json(this.mapConfig.mapJsonKey, this.mapConfig.mapJsonPath + cacheBust);
    this.load.image(this.mapConfig.backgrounds.bg1.key, this.mapConfig.backgrounds.bg1.path + cacheBust);
    this.load.image(this.mapConfig.backgrounds.bg2.key, this.mapConfig.backgrounds.bg2.path + cacheBust);
  }

  create() {
    if (this.loadingState && !this.loadingState.isComplete?.()) {
      this.loadingState.hide();
    }
    // ── Parse map data ──
    const mapData = this.cache.json.get(this.mapConfig.mapJsonKey);
    this.mapCols = mapData.width;
    this.mapRows = mapData.height;
    const worldW = mapData.width * mapData.tilewidth * MAP_SCALE;
    const worldH = mapData.height * mapData.tileheight * MAP_SCALE;
    const viewW = this.scale.width;
    const viewH = this.scale.height;
    const centerX = viewW / 2;

    // ── Build GID → atlas frame lookup ──
    this.gidMap = this.buildGidMap(mapData);

    // ── Backgrounds ──
    this.bg1 = this.add.tileSprite(0, 0, viewW, viewH, this.mapConfig.backgrounds.bg1.key)
      .setOrigin(0, 0).setScrollFactor(0).setDepth(-10);
    this.bg2 = this.add.tileSprite(0, 0, viewW, viewH, this.mapConfig.backgrounds.bg2.key)
      .setOrigin(0, 0).setScrollFactor(0).setDepth(-9);
    this.configureHorizontalOnlyTiling(this.bg1, this.mapConfig.backgrounds.bg1.key, viewH, 1.0);
    this.configureHorizontalOnlyTiling(this.bg2, this.mapConfig.backgrounds.bg2.key, viewH, 1.06);

    // ── Render map layers ──
    this.questBoardSprites = [];
    this.clearQuestBoardHints();
    this.renderTileLayer(mapData, "Ground", 0);
    this.renderObjectLayer(mapData, "Ground Decor", 0.5);
    this.renderObjectLayer(mapData, "Decor Back", 0.75);
    this.renderObjectLayer(mapData, "Buildings Back", 0.9);
    this.renderTileLayer(mapData, "Buildings Main (walls, roofs)", 1);
    this.renderObjectLayer(mapData, ["Buildings Front (trims, support)", "Buildings Front (trims, support, roofs)"], 2);
    this.renderObjectLayer(mapData, "Platforms", 2.2);
    this.renderObjectLayer(mapData, "Buildings Decor (doors, windows, ledges, accents)", 3);
    this.renderObjectLayer(mapData, "Decor (plants, rocks, signs)", 4);
    this.renderObjectLayer(mapData, ["Gameplay (sensors: ladders, interact zones)", "Gameplay (sensors: ladders, interact zones, spawn points)", "Gameplay"], 5);
    // Foreground objects must render above characters so players can pass behind them.
    this.renderObjectLayer(mapData, "Foreground (anything in front of player)", 3000);

    // ── Parse collision polygons ──
    this.collisionShapes = this.parseCollisionLayer(mapData);

    // ── Spawn point ──
    const entrySpawnName = this.scene.settings.data?.targetSpawnName || null;
    const spawnPos = resolveSpawnPosition(this, mapData, entrySpawnName);
    this.spawnX = spawnPos.x;
    this.spawnY = spawnPos.y;
    if (this.spawnUsedFallbackFromInvalidResume) {
      WORLD_STATE.lastStanding = { mapId: this.mapId, x: this.spawnX, y: this.spawnY };
      WORLD_STATE.lastStandingSavedAtMs = this.time.now;
      saveWorldStateToStorage();
    }

    // ── Animations ──
    for (const character of CHARACTER_OPTIONS) {
      for (const spec of BASE_ANIM_SPECS) {
        if (!characterSupportsState(character, spec.state)) continue;
        const atlas = composeAtlasKey(character.prefix, spec.suffix);
        if (!this.textures.exists(atlas)) continue;
        const key = composeAnimKey(character.id, spec.state);
        if (this.anims.exists(key)) continue;
        this.anims.create({
          key,
          frames: this.anims.generateFrameNames(atlas, {
            prefix: atlas + "_",
            start: 0,
            end: spec.end,
            zeroPad: 2,
          }),
          frameRate: 20,
          repeat: spec.repeat,
        });
      }
    }

    // Enemy animations (all types)
    for (const typeDef of Object.values(ENEMY_TYPES)) {
      for (const anim of typeDef.anims) {
        const key = typeDef.prefix + "_" + anim.suffix;
        if (!this.textures.exists(key)) continue;
        if (this.anims.exists(key)) continue;
        this.anims.create({
          key,
          frames: this.anims.generateFrameNames(key, { prefix: key + "_", start: 0, end: anim.end, zeroPad: 2 }),
          frameRate: 20,
          repeat: anim.repeat,
        });
      }
    }

    // NPC-only animations
    for (const typeDef of Object.values(NPC_TYPES)) {
      for (const anim of typeDef.anims) {
        const key = typeDef.prefix + "_" + anim.suffix;
        if (!this.textures.exists(key)) continue;
        if (this.anims.exists(key)) continue;
        this.anims.create({
          key,
          frames: this.anims.generateFrameNames(key, { prefix: key + "_", start: 0, end: anim.end, zeroPad: 2 }),
          frameRate: 20,
          repeat: anim.repeat,
        });
      }
    }

    this.calibrateCharacterAnchors();

    // ── Player entity ──
    this.playerEntity = new Player(this, this.spawnX, this.spawnY);
    this.renderPrevPlayerX = this.playerEntity.x;
    this.renderPrevPlayerY = this.playerEntity.y;
    this.renderCurrPlayerX = this.playerEntity.x;
    this.renderCurrPlayerY = this.playerEntity.y;

    this.playerSprite = this.add.sprite(
      this.spawnX + 14 - this.getCharacterCenterOffsetX(this.localCharacterId),
      this.spawnY,
      this.resolvePlayableAtlasForState(this.localCharacterId, "Idle"),
    );
    this.applySpriteOriginForCharacter(this.playerSprite, this.localCharacterId);
    this.playerSprite.setDepth(1100);
    this.localPlayerNameText = this.add.text(
      this.playerSprite.x,
      this.playerSprite.y - this.getCharacterFootOffset(this.localCharacterId) + this.remotePlayerLabelOffsetY,
      readPlayerName() || "Player",
      {
        fontFamily: "monospace",
        fontSize: "11px",
        color: "rgba(255,255,255,0.9)",
        stroke: "#000000",
        strokeThickness: 3,
      },
    ).setOrigin(0.5, 0).setDepth(1101 + this.playerSprite.y).setPadding(4, 1, 4, 1).setBackgroundColor("rgba(0,0,0,0.35)");
    const localIdleKey = this.resolvePlayableAnimKey(this.localCharacterId, "Idle");
    if (localIdleKey) this.playerSprite.play(localIdleKey);

    this.anim = {
      current: "Idle",
      prev: "",
      blinkTimer: 0,
      blinkInterval: 3000,
    };

    // ── Entity list ──
    this.entities = [this.playerEntity];
    this.effects = [];
    this.items = [];

    // Create food splat explosion animation
    this.anims.create({
      key: "food_splat",
      frames: Array.from({ length: 9 }, (_, i) => ({ key: `explosion_${i + 1}` })),
      frameRate: 28,
      repeat: 0,
    });

    const spawnNpcFromObject = (obj) => {
      const npcTypeRaw = getObjectProperty(obj, "npcType");
      const npcType = resolveNpcTypeDef(npcTypeRaw);
      const speedProp = Number(getObjectProperty(obj, "speed"));
      const patrolProp = Number(getObjectProperty(obj, "patrolDistance"));
      const npc = new Npc(
        this,
        obj.x * MAP_SCALE,
        (obj.y + obj.height) * MAP_SCALE,
        npcType,
        {
          speed: Number.isFinite(speedProp) ? speedProp : undefined,
          patrolDistance: Number.isFinite(patrolProp) ? patrolProp : undefined,
        }
      );
      npc.create();
      this.entities.push(npc);
    };

    const spawnedNpcIds = new Set();
    const enemySpawnAnchors = [];
    const enemySpawnPoints = [];
    const hasEnemySpawnNear = (x, y, minDistance = PLATFORM_ENEMY_SPAWN_MIN_GAP_PX) => {
      const minSq = minDistance * minDistance;
      return enemySpawnPoints.some((point) => {
        const dx = point.x - x;
        const dy = point.y - y;
        return (dx * dx + dy * dy) < minSq;
      });
    };
    const trackEnemySpawnPoint = (x, y) => {
      enemySpawnPoints.push({ x, y });
    };
    const spawnEnemyAt = (x, y, typeDef, { anchor = false } = {}) => {
      if (!Number.isFinite(x) || !Number.isFinite(y) || !typeDef) return false;
      if (hasEnemySpawnNear(x, y)) return false;
      const enemy = new Enemy(this, x, y, typeDef);
      enemy.create();
      this.entities.push(enemy);
      trackEnemySpawnPoint(x, y);
      if (anchor) enemySpawnAnchors.push({ x, y, typeDef });
      return true;
    };

    // Spawn enemies/NPCs/items from gameplay layer
    const gameplayLayer = mapData.layers.find(l => l.type === "objectgroup" && l.name.startsWith("Gameplay"));
    if (gameplayLayer) {
      for (const obj of gameplayLayer.objects) {
        const objTypeRaw = obj.properties?.find(p => p.name === "type")?.value;
        const enemyTypeRaw = obj.properties?.find(p => p.name === "enemyType")?.value;
        const objType = String(objTypeRaw || "").trim().toLowerCase();
        const markerType = String(objType || obj.type || obj.name || "").trim().toLowerCase();
        const resolvedEnemyType = String(enemyTypeRaw || objType || "").trim().toLowerCase();
        const isNpcSpawn = markerType === "npc_spawn";
        // Support both "target_dummy" (legacy) and enemy type keys like "golem_1".
        // Invalid legacy/non-golem types fall back to golem_1.
        const isEnemySpawnMarker =
          markerType === "enemy_spawn" ||
          markerType === "target_dummy" ||
          Object.prototype.hasOwnProperty.call(ENEMY_TYPES, markerType) ||
          LEGACY_ENEMY_TYPE_KEYS.has(markerType) ||
          Object.prototype.hasOwnProperty.call(ENEMY_TYPES, resolvedEnemyType) ||
          LEGACY_ENEMY_TYPE_KEYS.has(resolvedEnemyType);
        if (isEnemySpawnMarker && !isNpcSpawn) {
          const enemyTypeKey = Object.prototype.hasOwnProperty.call(ENEMY_TYPES, resolvedEnemyType)
            ? resolvedEnemyType
            : (Object.prototype.hasOwnProperty.call(ENEMY_TYPES, markerType) ? markerType : "golem_1");
          const typeDef = ENEMY_TYPES[enemyTypeKey] || ENEMY_TYPES.golem_1;
          if (
            markerType === "enemy_spawn" &&
            resolvedEnemyType &&
            !Object.prototype.hasOwnProperty.call(ENEMY_TYPES, resolvedEnemyType)
          ) {
            console.warn(`[${this.scene.key}] Unknown enemyType "${resolvedEnemyType}" on enemy_spawn id ${obj.id ?? "?"}; falling back to golem_1.`);
          } else if (
            markerType &&
            markerType !== "enemy_spawn" &&
            markerType !== "target_dummy" &&
            !Object.prototype.hasOwnProperty.call(ENEMY_TYPES, markerType) &&
            !LEGACY_ENEMY_TYPE_KEYS.has(markerType)
          ) {
            console.warn(`[${this.scene.key}] Unknown enemy marker "${markerType}" on object id ${obj.id ?? "?"}; falling back to golem_1.`);
          }
          spawnEnemyAt(obj.x * MAP_SCALE, (obj.y + obj.height) * MAP_SCALE, typeDef, { anchor: true });
        }

        if (isNpcSpawn) {
          spawnNpcFromObject(obj);
          if (obj.id != null) spawnedNpcIds.add(obj.id);
        }

        const collectibleType = String(objType || obj.name || "").toLowerCase();
        const itemTextureKey = COLLECTIBLE_TYPE_TO_TEXTURE[collectibleType];
        if (itemTextureKey) {
          spawnCollectible(this, itemTextureKey, obj.x * MAP_SCALE, (obj.y + obj.height) * MAP_SCALE);
        }
      }
    }

    if (enemySpawnAnchors.length > 0) {
      for (const anchor of enemySpawnAnchors) {
        const anchorGround = this.findGroundY(anchor.x, anchor.y - 16, anchor.y + 20, null);
        if (!anchorGround || !anchorGround.shape?.oneWay) continue;
        const heightRatio = clamp(1 - (anchorGround.y / Math.max(1, worldH)), 0, 1);
        const extraCount = 1 + Math.floor(heightRatio * PLATFORM_ENEMY_REINFORCEMENT_MAX_PER_ANCHOR);
        let spawned = 0;
        const maxSlots = extraCount * 4;
        for (let slot = 1; slot <= maxSlots && spawned < extraCount; slot++) {
          const dir = slot % 2 === 0 ? -1 : 1;
          const rank = Math.ceil(slot / 2);
          const candidateX = clamp(
            anchor.x + dir * PLATFORM_ENEMY_REINFORCEMENT_SPACING_PX * rank,
            24,
            worldW - 24,
          );
          const candidateGround = this.findGroundY(
            candidateX,
            anchorGround.y - 18,
            anchorGround.y + 24,
            null,
          );
          if (!candidateGround || candidateGround.shape !== anchorGround.shape) continue;
          if (!spawnEnemyAt(candidateX, candidateGround.y, anchor.typeDef)) continue;
          spawned++;
        }
      }
    }

    // Also accept npc_spawn markers from non-Gameplay object layers (for map-editing
    // flexibility), while avoiding duplicate spawns.
    for (const layer of mapData.layers) {
      if (layer.type !== "objectgroup") continue;
      for (const obj of layer.objects || []) {
        const objType = getObjectProperty(obj, "type");
        const isNpcSpawn = String(objType || obj.name || "").toLowerCase() === "npc_spawn";
        if (!isNpcSpawn) continue;
        if (obj.id != null && spawnedNpcIds.has(obj.id)) continue;
        spawnNpcFromObject(obj);
        if (obj.id != null) spawnedNpcIds.add(obj.id);
      }
    }

    // Temporary test path: random pickups so item/heal loop can be tested
    // before map-authored spawn points are fully added.
    if (this.mapConfig.enableRandomTestItems) {
      spawnRandomTestCollectibles(this, worldW, worldH, TEMP_RANDOM_ITEM_COUNT, COLLECTIBLE_TEXTURE_KEYS);
    }

    this.portalZones = parsePortalZones(mapData);
    this.portalCooldownMs = 0;

    // ── Edge-trigger flags ──
    this.prevKeys = {
      jump: false,
      dash: false,
      slash: false,
      throw: false,
      kick: false,
      hurt: false,
      debug: false,
    };
    this.simAccumulatorMs = 0;
    this.simLastTimeMs = this.time.now;
    this.lastSimStepCount = 0;
    this.lastPlayerAttackHitbox = null;
    this.renderLerpAlpha = 0;
    this.renderPrevPlayerX = this.playerEntity.x;
    this.renderPrevPlayerY = this.playerEntity.y;
    this.renderCurrPlayerX = this.playerEntity.x;
    this.renderCurrPlayerY = this.playerEntity.y;
    this.showDebugHitboxes = false;

    // ── Input ──
    this.cursors = this.input.keyboard.createCursorKeys();
    this.keyF = this.input.keyboard.addKey("F");
    this.keyD = this.input.keyboard.addKey("D");
    this.keyA = this.input.keyboard.addKey("A");
    this.keyS = this.input.keyboard.addKey("S");
    this.keyH = this.input.keyboard.addKey("H");
    this.keyR = this.input.keyboard.addKey("R");
    this.keyT = this.input.keyboard.addKey("T");
    this.keyF3 = this.input.keyboard.addKey("F3");
    this.keyEnter = this.input.keyboard.addKey("ENTER");
    this.keyEsc = this.input.keyboard.addKey("ESC");

    // ── Camera ──
    this.cameras.main.setBounds(0, 0, worldW, worldH);
    this.cameras.main.roundPixels = true;

    // ── HUD/UI ──
    this.ui = createHUDController(this, {
      viewW,
      viewH,
      centerX,
      onLoadingTest: () => {
        const triggerLoadingTest = globalThis.startTestLoadingState;
        if (typeof triggerLoadingTest === "function") {
          triggerLoadingTest();
        }
      },
      onProfileEdit: async () => {
        if (typeof globalThis.openPlayerProfileModal !== "function") return;
        const profile = await globalThis.openPlayerProfileModal();
        if (!profile) return;
        window.location.reload();
      },
    });
    this.netStatusText = this.ui?.netStatusText || null;
    this.refreshChatHud();
    this.chatInputHandler = (event) => {
      if (this.bulletinBoardOpen) return;
      if (!this.chatOpen) {
        const key = String(event?.key || "").toLowerCase();
        if (key === "c" && !event.ctrlKey && !event.metaKey && !event.altKey) {
          event.preventDefault?.();
          this.toggleChatCollapsed();
          return;
        }
      }
      if (this.chatOpen) {
        const blocked = event?.key;
        if (blocked === "ArrowLeft" || blocked === "ArrowRight" || blocked === "ArrowUp" || blocked === "ArrowDown" ||
            blocked === "Tab" || blocked === "Enter" || blocked === "Escape") {
          event.preventDefault?.();
          return;
        }
        if (event.key === "Backspace") {
          this.chatInputText = this.chatInputText.slice(0, -1);
          this.refreshChatHud();
          return;
        }
        if (event.key === "Tab" || event.key === "ArrowLeft" || event.key === "ArrowRight" || event.key === "ArrowUp" || event.key === "ArrowDown") {
          return;
        }
        if (event.key && event.key.length === 1) {
          if (event.ctrlKey || event.metaKey || event.altKey) return;
          this.chatInputText = `${this.chatInputText}${event.key}`.slice(0, CHAT_MAX_TEXT_LENGTH);
          this.refreshChatHud();
        }
        return;
      }
    };
    this.input.keyboard.on("keydown", this.chatInputHandler);

    if (typeof WORLD_STATE.hp === "number") {
      this.playerEntity.hp = Math.max(0, Math.min(this.playerEntity.maxHp, WORLD_STATE.hp));
    }
    WORLD_STATE.lastMapId = this.mapId;

    this.initNetworking();
    this.events.once("shutdown", () => this.teardownNetworking());
    this.events.once("destroy", () => this.teardownNetworking());

    // ── Debug graphics ──
    this.debugGfx = this.add.graphics().setDepth(50);
  }

  openChat() {
    if (this.chatOpen) return;
    this.chatOpen = true;
    if (this.chatInputText === null || this.chatInputText === undefined) this.chatInputText = "";
    this.ui?.drawChat?.({
      open: true,
      inputText: this.chatInputText,
      collapsed: this.chatCollapsed,
      history: this.chatHistory,
      maxLines: this.chatMaxHistory,
    });
  }

  closeChat() {
    this.chatOpen = false;
    this.chatInputText = "";
    this.ui?.drawChat?.({
      open: false,
      inputText: "",
      history: this.chatHistory,
      collapsed: this.chatCollapsed,
      maxLines: this.chatMaxHistory,
    });
  }

  submitChat() {
    if (!this.chatOpen) return;
    const text = String(this.chatInputText || "").trim();
    this.chatInputText = "";
    this.chatOpen = false;
    if (text.length > 0 && this.netClient) {
      this.netClient.sendChat(text);
    }
    this.ui?.drawChat?.({
      open: false,
      inputText: this.chatInputText,
      collapsed: this.chatCollapsed,
      history: this.chatHistory,
      maxLines: this.chatMaxHistory,
    });
  }

  refreshChatHud() {
    this.ui?.drawChat?.({
      open: this.chatOpen,
      inputText: this.chatInputText,
      collapsed: this.chatCollapsed,
      history: this.chatHistory,
      maxLines: this.chatMaxHistory,
    });
  }

  applyBulletinBoardSnapshot(payload) {
    const sourceMessages = Array.isArray(payload?.messages) ? payload.messages : [];
    this.bulletinBoardMessages = normalizeBulletinBoardMessages(sourceMessages);
    this.refreshBulletinBoardModal();
  }

  refreshBulletinBoardModal() {
    if (!this.bulletinBoardModalController) return;
    this.bulletinBoardModalController.update({
      messages: this.bulletinBoardMessages,
      localPlayerId: this.netPlayerId,
    });
  }

  openBulletinBoard() {
    if (typeof globalThis.openQuestBoardModal !== "function") return;
    if (this.bulletinBoardModalController) {
      this.refreshBulletinBoardModal();
      return;
    }

    this.bulletinBoardOpen = true;
    this.closeChat();
    this.ui?.setInteractTextVisible?.(false);
    this.bulletinBoardModalController = globalThis.openQuestBoardModal({
      localPlayerId: this.netPlayerId,
      messages: this.bulletinBoardMessages,
      onCreate: (text) => {
        if (!this.netClient) return;
        this.netClient.sendBulletinBoardCreate(text);
      },
      onUpdate: (messageId, text) => {
        if (!this.netClient) return;
        this.netClient.sendBulletinBoardUpdate(messageId, text);
      },
      onDelete: (messageId) => {
        if (!this.netClient) return;
        this.netClient.sendBulletinBoardDelete(messageId);
      },
      onClose: () => {
        this.bulletinBoardOpen = false;
        this.bulletinBoardModalController = null;
      },
    });
  }

  closeBulletinBoard() {
    if (!this.bulletinBoardModalController) {
      this.bulletinBoardOpen = false;
      return;
    }
    this.bulletinBoardModalController.close();
    this.bulletinBoardModalController = null;
    this.bulletinBoardOpen = false;
  }

  updateScoreboardFromState(players) {
    if (!Array.isArray(players)) {
      this.resetScoreboard();
      return;
    }

    const now = Date.now();
    const byId = new Map();
    const liveEntries = [];

    for (const player of players) {
      if (!player || !player.id) continue;
      const playerId = String(player.id);
      if (byId.has(playerId)) continue;
      byId.set(playerId, true);
      const stats = player.stats || {};
      const record = {
        id: playerId,
        name: sanitizeScoreboardName(player.name),
        playerKills: normalizeScore(stats.playerKills),
        playerDeaths: normalizeScore(stats.playerDeaths),
        enemyKills: normalizeScore(stats.enemyKills),
        enemyDeaths: normalizeScore(stats.enemyDeaths),
        lastSeenAt: now,
      };
      const merged = mergeScoreboardRecord(this.scoreboardKnownPlayers.get(playerId), record);
      this.scoreboardKnownPlayers.set(playerId, merged);
      liveEntries.push(merged);
    }

    for (const [playerId, existing] of this.scoreboardKnownPlayers.entries()) {
      if (!byId.has(playerId)) continue;
      existing.lastSeenAt = now;
      existing.name = sanitizeScoreboardName(existing.name || SCOREBOARD_UNKNOWN_NAME);
      existing.playerKills = normalizeScore(existing.playerKills);
      existing.playerDeaths = normalizeScore(existing.playerDeaths);
      existing.enemyKills = normalizeScore(existing.enemyKills);
      existing.enemyDeaths = normalizeScore(existing.enemyDeaths);
      this.scoreboardKnownPlayers.set(playerId, {
        ...existing,
        name: existing.name,
        playerKills: existing.playerKills,
        playerDeaths: existing.playerDeaths,
        enemyKills: existing.enemyKills,
        enemyDeaths: existing.enemyDeaths,
      });
    }

    const offlineRows = Array.from(this.scoreboardKnownPlayers.values()).filter((entry) => !byId.has(String(entry.id)));
    const bySection = [...liveEntries, ...offlineRows];
    const normalizedAll = applyScoreboardDisambiguation(bySection);
    const normalizedLive = normalizedAll.filter((entry) => byId.has(String(entry.id)));
    const normalizedOffline = normalizedAll.filter((entry) => !byId.has(String(entry.id)));
    this.scoreboardDisplayNameById = new Map(normalizedAll.map((entry) => [String(entry.id), entry.displayName || entry.name]));

    const byIdSet = new Set(normalizedLive.map((entry) => String(entry.id)));
    this.scoreboardRows = normalizedLive.sort((a, b) => {
      if (b.playerKills !== a.playerKills) return b.playerKills - a.playerKills;
      if (a.playerDeaths !== b.playerDeaths) return a.playerDeaths - b.playerDeaths;
      if (b.enemyKills !== a.enemyKills) return b.enemyKills - a.enemyKills;
      return (a.name || "").localeCompare(b.name || "");
    });
    this.scoreboardOfflineRows = normalizedOffline
      .sort((a, b) => {
        if (b.playerKills !== a.playerKills) return b.playerKills - a.playerKills;
        if (a.playerDeaths !== b.playerDeaths) return a.playerDeaths - b.playerDeaths;
        if (b.enemyKills !== a.enemyKills) return b.enemyKills - a.enemyKills;
        return (a.name || "").localeCompare(b.name || "");
      })
      ;
    this.scoreboardRows = this.scoreboardRows.slice(0, SCOREBOARD_MAX_ROWS);
    this.scoreboardOfflineRows = this.scoreboardOfflineRows.filter((entry) => !byIdSet.has(String(entry.id)));

    saveScoreboardOfflineHistory(this.scoreboardKnownPlayers);
    this.ui?.setScoreboard?.({
      players: this.scoreboardRows,
      offlinePlayers: this.scoreboardOfflineRows,
      localPlayerId: this.netPlayerId,
    });
    this.netStartupPlayersReady = true;
    this.updateNetworkStartupPhase();
  }

  applyWelcomeScoreboardSnapshot(payload) {
    const incomingRows = Array.isArray(payload?.players) ? payload.players : [];
    const serverResetVersion = Number.isFinite(Number(payload?.resetVersion))
      ? Math.max(0, Math.floor(Number(payload.resetVersion)))
      : 0;
    const storedResetVersion = readScoreboardServerResetVersion(this.scoreboardApiUrl);
    if (serverResetVersion > storedResetVersion) {
      this.scoreboardKnownPlayers.clear();
      this.scoreboardServerSyncSignature = "";
      saveScoreboardOfflineHistory(this.scoreboardKnownPlayers);
      this.resetScoreboard();
    }
    this.scoreboardServerResetVersion = Math.max(storedResetVersion, serverResetVersion);
    writeScoreboardServerResetVersion(this.scoreboardApiUrl, this.scoreboardServerResetVersion);

    let changed = false;
    for (const rawRecord of incomingRows) {
      const normalized = normalizeAnyScoreboardRecord(rawRecord);
      if (!normalized) continue;
      const merged = mergeScoreboardRecord(this.scoreboardKnownPlayers.get(normalized.id), normalized);
      this.scoreboardKnownPlayers.set(normalized.id, merged);
      changed = true;
    }
    if (changed) {
      saveScoreboardOfflineHistory(this.scoreboardKnownPlayers);
    }
    this.scoreboardServerLoaded = true;
    this.scoreboardLastFetchedAt = Date.now();
    this.netStartupScoreboardReady = true;
    this.updateNetworkStartupPhase();
  }

  applyLocalScoreEvent(event) {
    const safeEvent = typeof event === "string" ? event.trim().toLowerCase() : "";
    if (safeEvent !== "enemy_kill" && safeEvent !== "enemy_death") return;
    if (!this.netPlayerId) return;

    const playerId = String(this.netPlayerId);
    const existing = this.scoreboardKnownPlayers.get(playerId) || normalizeScoreboardRecord({
      id: playerId,
      name: this.localPlayerNameText?.text || SCOREBOARD_UNKNOWN_NAME,
      playerKills: 0,
      playerDeaths: 0,
      enemyKills: 0,
      enemyDeaths: 0,
      lastSeenAt: Date.now(),
    });
    if (!existing) return;

    const updated = {
      ...existing,
      name: sanitizeScoreboardName(existing.name || this.localPlayerNameText?.text || SCOREBOARD_UNKNOWN_NAME),
      enemyKills: normalizeScore(existing.enemyKills) + (safeEvent === "enemy_kill" ? 1 : 0),
      enemyDeaths: normalizeScore(existing.enemyDeaths) + (safeEvent === "enemy_death" ? 1 : 0),
      lastSeenAt: Date.now(),
    };
    this.scoreboardKnownPlayers.set(playerId, mergeScoreboardRecord(existing, updated));
    saveScoreboardOfflineHistory(this.scoreboardKnownPlayers);

    const liveIds = new Set(this.scoreboardRows.map((entry) => String(entry.id)));
    liveIds.add(playerId);
    const knownRows = Array.from(this.scoreboardKnownPlayers.values());
    const normalized = applyScoreboardDisambiguation(knownRows);
    this.scoreboardDisplayNameById = new Map(normalized.map((entry) => [String(entry.id), entry.displayName || entry.name]));

    this.scoreboardRows = normalized
      .filter((entry) => liveIds.has(String(entry.id)))
      .sort((a, b) => {
        if (b.playerKills !== a.playerKills) return b.playerKills - a.playerKills;
        if (a.playerDeaths !== b.playerDeaths) return a.playerDeaths - b.playerDeaths;
        if (b.enemyKills !== a.enemyKills) return b.enemyKills - a.enemyKills;
        return (a.name || "").localeCompare(b.name || "");
      })
      .slice(0, SCOREBOARD_MAX_ROWS);

    const shownLive = new Set(this.scoreboardRows.map((entry) => String(entry.id)));
    this.scoreboardOfflineRows = normalized
      .filter((entry) => !shownLive.has(String(entry.id)))
      .sort((a, b) => {
        if (b.playerKills !== a.playerKills) return b.playerKills - a.playerKills;
        if (a.playerDeaths !== b.playerDeaths) return a.playerDeaths - b.playerDeaths;
        if (b.enemyKills !== a.enemyKills) return b.enemyKills - a.enemyKills;
        return (a.name || "").localeCompare(b.name || "");
      });

    this.ui?.setScoreboard?.({
      players: this.scoreboardRows,
      offlinePlayers: this.scoreboardOfflineRows,
      localPlayerId: this.netPlayerId,
    });
  }

  resetScoreboard() {
    this.scoreboardRows = [];
    this.scoreboardOfflineRows = [];
    this.scoreboardDisplayNameById.clear();
    if (this.ui?.resetScoreboard) {
      this.ui.resetScoreboard();
    } else {
      this.ui?.setScoreboard?.({
        players: [],
        offlinePlayers: [],
        localPlayerId: null,
      });
    }
  }

  async fetchServerScoreboard(force = false) {
    if (!this.scoreboardApiUrl || typeof globalThis?.fetch !== "function") return;
    if (this.scoreboardServerFetchInFlight) return;
    if (this.scoreboardServerLoaded && !force) return;
    if (!force && Number.isFinite(this.scoreboardLastFetchedAt)) {
      const ageMs = Date.now() - this.scoreboardLastFetchedAt;
      if (ageMs >= 0 && ageMs < SCOREBOARD_FETCH_MIN_INTERVAL_MS) return;
    }
    this.scoreboardServerFetchInFlight = true;
    try {
      const response = await globalThis.fetch(`${this.scoreboardApiUrl}?limit=${SCOREBOARD_OFFLINE_HISTORY_MAX}`, {
        method: "GET",
      });
      if (!response.ok) return;
      const payload = await response.json().catch(() => null);
      const serverResetVersion = Number.isFinite(Number(payload?.resetVersion))
        ? Math.max(0, Math.floor(Number(payload.resetVersion)))
        : 0;
      const storedResetVersion = readScoreboardServerResetVersion(this.scoreboardApiUrl);
      if (serverResetVersion > storedResetVersion) {
        this.scoreboardKnownPlayers.clear();
        this.scoreboardServerSyncSignature = "";
        saveScoreboardOfflineHistory(this.scoreboardKnownPlayers);
        this.resetScoreboard();
      }
      this.scoreboardServerResetVersion = Math.max(storedResetVersion, serverResetVersion);
      writeScoreboardServerResetVersion(this.scoreboardApiUrl, this.scoreboardServerResetVersion);
      const incomingRows = Array.isArray(payload?.players) ? payload.players : [];
      let changed = false;
      for (const rawRecord of incomingRows) {
        const normalized = normalizeAnyScoreboardRecord(rawRecord);
        if (!normalized) continue;
        const merged = mergeScoreboardRecord(this.scoreboardKnownPlayers.get(normalized.id), normalized);
        this.scoreboardKnownPlayers.set(normalized.id, merged);
        changed = true;
      }
      if (changed) {
        saveScoreboardOfflineHistory(this.scoreboardKnownPlayers);
      }
      this.scoreboardServerLoaded = true;
      this.scoreboardLastFetchedAt = Date.now();
      this.netStartupScoreboardReady = true;
      this.updateNetworkStartupPhase();
    } catch {
      // no-op
    } finally {
      this.scoreboardServerFetchInFlight = false;
    }
  }

  queueScoreboardServerSync() {
    // Disabled intentionally.
  }

  async syncScoreboardToServer() {
    // Disabled intentionally.
  }

  getDisplayNameForChatPlayer(playerId, fallbackName, isSelf) {
    if (isSelf) return "You";
    const id = playerId !== null && playerId !== undefined ? String(playerId) : null;
    if (id && this.scoreboardDisplayNameById.has(id)) {
      const resolved = this.scoreboardDisplayNameById.get(id);
      if (resolved) return resolved;
    }
    return sanitizeScoreboardName(fallbackName);
  }

  toggleChatCollapsed() {
    if (this.chatOpen) return;
    this.chatCollapsed = !this.chatCollapsed;
    this.refreshChatHud();
  }

  normalizePlayerBubbleId(playerId) {
    if (playerId === null || playerId === undefined) return null;
    return String(playerId);
  }

  destroyChatBubble(playerId) {
    const key = this.normalizePlayerBubbleId(playerId);
    if (!key) return;
    const bubble = this.chatBubbles.get(key);
    if (!bubble) return;
    bubble.container?.destroy();
    this.chatBubbles.delete(key);
  }

  setChatBubbleMessage(playerId, text, now = this.time?.now) {
    const key = this.normalizePlayerBubbleId(playerId);
    if (!key) return;
    const normalizedText = String(text || "").slice(0, CHAT_MAX_TEXT_LENGTH).trim();
    if (!normalizedText) return;
    const bubble = this.chatBubbles.get(key);
    const sceneNow = Number.isFinite(this.time?.now) ? this.time.now : Date.now();
    const incomingNow = Number.isFinite(now) ? Number(now) : sceneNow;
    const nowMs = incomingNow > 1e10 ? sceneNow : incomingNow;
    const expiresAt = nowMs + CHAT_BUBBLE_TTL_MS;

    if (bubble) {
      bubble.expiresAt = expiresAt;
      bubble.text.setText(normalizedText);
      bubble.rawText = normalizedText;
      this.layoutChatBubble(bubble);
      return;
    }

    const bubbleText = this.add.text(0, 0, normalizedText, {
      ...this.chatBubbleTextConfig,
      wordWrap: {
        width: CHAT_BUBBLE_MAX_WIDTH - CHAT_BUBBLE_PADDING_X * 2,
        useAdvancedWrap: true,
      },
      padding: { left: CHAT_BUBBLE_PADDING_X, right: CHAT_BUBBLE_PADDING_X, top: CHAT_BUBBLE_PADDING_Y, bottom: CHAT_BUBBLE_PADDING_Y },
    });
    bubbleText.setOrigin(0, 0);

    const bubbleBg = this.add.graphics();
    const bubbleContainer = this.add.container(0, 0, [bubbleBg, bubbleText]).setDepth(CHAT_BUBBLE_DEPTH);

    const newBubble = {
      container: bubbleContainer,
      bg: bubbleBg,
      text: bubbleText,
      rawText: normalizedText,
      width: 0,
      height: 0,
      expiresAt,
      playerId: key,
    };
    this.chatBubbles.set(key, newBubble);
    this.layoutChatBubble(newBubble);
  }

  layoutChatBubble(bubble) {
    if (!bubble?.text || !bubble?.bg) return;

    const maxTextWidth = Math.max(48, CHAT_BUBBLE_MAX_WIDTH - CHAT_BUBBLE_PADDING_X * 2);
    bubble.text.setStyle({
      ...this.chatBubbleTextConfig,
      wordWrap: {
        width: maxTextWidth,
        useAdvancedWrap: true,
      },
      padding: { left: CHAT_BUBBLE_PADDING_X, right: CHAT_BUBBLE_PADDING_X, top: CHAT_BUBBLE_PADDING_Y, bottom: CHAT_BUBBLE_PADDING_Y },
    });
    bubble.text.setText(bubble.rawText || "");

    const textWidth = Math.min(maxTextWidth, Math.ceil(bubble.text.width));
    const textHeight = Math.ceil(bubble.text.height);
    const width = Math.max(48, textWidth + CHAT_BUBBLE_PADDING_X * 2);
    const height = Math.max(26, textHeight + CHAT_BUBBLE_PADDING_Y * 2);

    bubble.width = width;
    bubble.height = height;
    bubble.text.setPosition(CHAT_BUBBLE_PADDING_X, CHAT_BUBBLE_PADDING_Y);

    bubble.bg.clear();
    bubble.bg.fillStyle(0x10131d, 0.78);
    bubble.bg.fillRoundedRect(0, 0, width, height, 8);
    bubble.bg.lineStyle(1, 0xbcc7f5, 0.45);
    bubble.bg.strokeRoundedRect(0, 0, width, height, 8);
    bubble.bg.fillStyle(0x10131d, 0.7);
    bubble.bg.fillTriangle(
      width / 2 - 6,
      height,
      width / 2 + 6,
      height,
      width / 2,
      height + 7,
    );
    bubble.bg.lineStyle(1, 0xbcc7f5, 0.35);
    bubble.bg.strokeTriangle(
      width / 2 - 6,
      height,
      width / 2 + 6,
      height,
      width / 2,
      height + 7,
    );
  }

  resolveChatBubbleAnchor(playerId) {
    const key = this.normalizePlayerBubbleId(playerId);
    if (!key) return null;

    const localId = this.netPlayerId != null ? String(this.netPlayerId) : null;
    if (localId && key === localId && this.playerSprite) {
      const charId = this.sanitizeCharacterId(this.localCharacterId);
      const footOffset = this.getCharacterFootOffset(charId);
      return {
        x: this.playerSprite.x,
        y: this.playerSprite.y - footOffset - CHAT_BUBBLE_ANCHOR_OFFSET_Y,
      };
    }

    const remote = this.remotePlayers.get(key);
    if (!remote?.sprite) return null;
    const characterId = this.sanitizeCharacterId(remote.characterId || this.localCharacterId);
    const footOffset = this.getCharacterFootOffset(characterId);
    return {
      x: remote.sprite.x,
      y: remote.sprite.y - footOffset - CHAT_BUBBLE_ANCHOR_OFFSET_Y,
    };
  }

  updateChatBubbles(now) {
    const nowMs = Number.isFinite(now) ? now : this.time.now;
    for (const [playerId, bubble] of this.chatBubbles.entries()) {
      if (!bubble) {
        this.chatBubbles.delete(playerId);
        continue;
      }

      if (nowMs >= bubble.expiresAt) {
        this.destroyChatBubble(playerId);
        continue;
      }

      const anchor = this.resolveChatBubbleAnchor(playerId);
      if (!anchor) {
        bubble.container?.setVisible(false);
        continue;
      }

      bubble.container?.setVisible(true);
      bubble.container?.setPosition(anchor.x - bubble.width / 2, anchor.y - bubble.height);
    }
  }

  clearAllChatBubbles() {
    for (const playerId of [...this.chatBubbles.keys()]) {
      this.destroyChatBubble(playerId);
    }
  }

  // ── Map helpers ──

  buildGidMap(mapData) {
    const gidMap = {};
    for (const ts of mapData.tilesets) {
      const atlasKey = ts.name;
      if (!ts.tiles) continue;
      for (const tile of ts.tiles) {
        const gid = ts.firstgid + tile.id;
        let frameName = tile.properties?.find(p => p.name === "name")?.value;
        if (!frameName && tile.image) {
          // Fallback for Tiled tiles that don't carry a `name` property:
          // derive atlas frame from the tile image filename.
          const parts = String(tile.image).split("/");
          const file = parts[parts.length - 1] || "";
          frameName = file.replace(/\.[^.]+$/, "");
        }
        if (frameName) {
          gidMap[gid] = { atlas: atlasKey, frame: frameName, w: tile.imagewidth, h: tile.imageheight };
        }
      }
    }
    return gidMap;
  }

  findLayer(mapData, layerType, layerNames) {
    const candidates = asLayerNameList(layerNames);
    for (const candidate of candidates) {
      const exact = mapData.layers.find(l =>
        l.type === layerType &&
        typeof l.name === "string" &&
        l.name.toLowerCase() === candidate.toLowerCase()
      );
      if (exact) return exact;
    }
    for (const candidate of candidates) {
      const prefix = mapData.layers.find(l =>
        l.type === layerType &&
        typeof l.name === "string" &&
        l.name.toLowerCase().startsWith(candidate.toLowerCase())
      );
      if (prefix) return prefix;
    }
    return null;
  }

  renderTileLayer(mapData, layerName, depth) {
    const layer = this.findLayer(mapData, "tilelayer", layerName);
    if (!layer) return;
    const tw = mapData.tilewidth;
    const th = mapData.tileheight;
    for (let i = 0; i < layer.data.length; i++) {
      const rawGid = layer.data[i];
      if (rawGid === 0) continue;
      const tgid = parseTiledGid(rawGid);
      if (tgid.gid === 0) continue;
      const info = this.gidMap[tgid.gid];
      if (!info) continue;
      const col = i % layer.width;
      const row = Math.floor(i / layer.width);
      const x = col * tw * MAP_SCALE;
      const y = (row + 1) * th * MAP_SCALE;
      const img = this.add.image(x, y, info.atlas, info.frame).setOrigin(0, 1).setScale(MAP_SCALE).setDepth(depth);
      img.setFlip(tgid.flipX, tgid.flipY);
      if (tgid.flipD) img.setAngle(90);
    }
  }

  createQuestBoardHint(boardX, boardY, boardWidth, boardHeight) {
    const centerX = boardX + boardWidth * 0.5;
    const topY = boardY - boardHeight;
    const label = this.add.text(centerX, topY - 10, QUEST_BOARD_HINT_TEXT, {
      fontFamily: "monospace",
      fontSize: "12px",
      color: "rgba(255,255,255,0.95)",
      stroke: "#000000",
      strokeThickness: 3,
      align: "center",
    })
      .setOrigin(0.5, 1)
      .setDepth(QUEST_BOARD_HINT_DEPTH)
      .setPadding(6, 2, 6, 2)
      .setBackgroundColor("rgba(0,0,0,0.45)");

    const tween = this.tweens.add({
      targets: label,
      y: label.y - QUEST_BOARD_HINT_BOB_PX,
      duration: 1200,
      yoyo: true,
      repeat: -1,
      ease: "Sine.easeInOut",
    });

    this.questBoardHints.push({ label, tween });
  }

  clearQuestBoardHints() {
    for (const hint of this.questBoardHints) {
      if (hint?.tween) {
        hint.tween.stop();
        hint.tween.remove();
      }
      hint?.label?.destroy();
    }
    this.questBoardHints = [];
  }

  renderObjectLayer(mapData, layerName, depth) {
    const layer = this.findLayer(mapData, "objectgroup", layerName);
    if (!layer) return;
    for (const obj of layer.objects) {
      if (!obj.gid) continue;
      const tgid = parseTiledGid(obj.gid);
      const info = this.gidMap[tgid.gid];
      if (!info) continue;
      const img = this.add.image(obj.x * MAP_SCALE, obj.y * MAP_SCALE, info.atlas, info.frame)
        .setOrigin(0, 1)
        .setScale(MAP_SCALE)
        .setDepth(depth);
      img.setFlip(tgid.flipX, tgid.flipY);
      if (tgid.flipD) img.setAngle(90);
      if (obj.rotation) img.setAngle(img.angle + obj.rotation);

      const frameName = String(info.frame || "").toLowerCase();
      if (frameName === QUEST_BOARD_FRAME_NAME) {
        img.setInteractive({ useHandCursor: true });
        img.on("pointerdown", () => this.openBulletinBoard());
        this.questBoardSprites.push(img);
        const boardW = Number.isFinite(Number(info.w)) ? Number(info.w) * MAP_SCALE : Number(obj.width || 0) * MAP_SCALE;
        const boardH = Number.isFinite(Number(info.h)) ? Number(info.h) * MAP_SCALE : Number(obj.height || 0) * MAP_SCALE;
        this.createQuestBoardHint(obj.x * MAP_SCALE, obj.y * MAP_SCALE, boardW, boardH);
      }
    }
  }

  configureHorizontalOnlyTiling(tileSprite, textureKey, viewHeight, fitMultiplier = 1) {
    const img = this.textures.get(textureKey)?.getSourceImage();
    if (!img || !img.height) return;
    const yScale = (viewHeight / img.height) * fitMultiplier;
    // Force exactly one tile vertically by scaling texture height to viewport height.
    // Width may repeat as needed.
    tileSprite.setTileScale(yScale, yScale);
  }

  parseCollisionLayer(mapData) {
    const shapes = [];
    this.ladderShapes = [];

    // Parse ladders from gameplay layer
    const gameplayLayer = mapData.layers.find(l => l.type === "objectgroup" && l.name.startsWith("Gameplay"));
    if (gameplayLayer) {
      for (const obj of gameplayLayer.objects) {
        const objType = obj.properties?.find(p => p.name === "type")?.value;
        if (objType === "ladder") {
          this.ladderShapes.push({
            x: obj.x * MAP_SCALE,
            y: obj.y * MAP_SCALE,
            w: obj.width * MAP_SCALE,
            h: obj.height * MAP_SCALE,
            centerX: (obj.x + obj.width / 2) * MAP_SCALE,
          });
        }
      }
    }

    // Parse collision shapes
    const layer = mapData.layers.find(l => l.name === "Collision" && l.type === "objectgroup");
    if (!layer) return shapes;

    const getProp = (obj, key) => obj.properties?.find(p => p.name === key)?.value;
    const rotatePoint = (px, py, ox, oy, rad) => {
      const dx = px - ox;
      const dy = py - oy;
      return {
        x: ox + dx * Math.cos(rad) - dy * Math.sin(rad),
        y: oy + dx * Math.sin(rad) + dy * Math.cos(rad),
      };
    };

    for (const obj of layer.objects) {
      let rawVertices = null;
      if (obj.polygon?.length) {
        rawVertices = obj.polygon.map(p => ({ x: obj.x + p.x, y: obj.y + p.y }));
      } else if (obj.width > 0 && obj.height > 0) {
        rawVertices = [
          { x: obj.x, y: obj.y },
          { x: obj.x + obj.width, y: obj.y },
          { x: obj.x + obj.width, y: obj.y + obj.height },
          { x: obj.x, y: obj.y + obj.height },
        ];
        if (obj.rotation) {
          const rad = (obj.rotation * Math.PI) / 180;
          rawVertices = rawVertices.map(v => rotatePoint(v.x, v.y, obj.x, obj.y, rad));
        }
      } else {
        continue;
      }

      const objType = String(getProp(obj, "type") || "").toLowerCase();
      const oneWayProp = getProp(obj, "oneWay");
      const oneWay = oneWayProp === true || String(oneWayProp).toLowerCase() === "true";
      const platform = objType === "platform" || oneWay;

      const vertices = rawVertices.map(v => ({
        x: v.x * MAP_SCALE,
        y: v.y * MAP_SCALE,
      }));

      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const v of vertices) {
        if (v.x < minX) minX = v.x;
        if (v.y < minY) minY = v.y;
        if (v.x > maxX) maxX = v.x;
        if (v.y > maxY) maxY = v.y;
      }

      const edges = [];
      for (let i = 0; i < vertices.length; i++) {
        edges.push({ a: vertices[i], b: vertices[(i + 1) % vertices.length] });
      }

      shapes.push({ name: obj.name, vertices, edges, bounds: { minX, minY, maxX, maxY }, oneWay: platform });
    }
    return shapes;
  }

  // ── Ladder detection (parameterized for any entity) ──

  isOnLadder(entity) {
    const pLeft = entity.x;
    const pRight = entity.x + entity.w;
    const pTop = entity.y;
    const pBottom = entity.y + entity.h;
    const minOverlap = entity.w * 0.7;

    for (const ladder of this.ladderShapes) {
      if (pBottom <= ladder.y || pTop >= ladder.y + ladder.h) continue;
      const overlap = Math.min(pRight, ladder.x + ladder.w) - Math.max(pLeft, ladder.x);
      if (overlap >= minOverlap) return true;
    }
    return false;
  }

  getLadderCenterX(entity) {
    for (const ladder of this.ladderShapes) {
      if (entity.y + entity.h <= ladder.y || entity.y >= ladder.y + ladder.h) continue;
      if (entity.x + entity.w <= ladder.x || entity.x >= ladder.x + ladder.w) continue;
      return ladder.centerX;
    }
    return -1;
  }

  // ── Ground raycast ──

  findGroundY(px, minY, maxY, excludeShape) {
    let bestY = Infinity;
    let bestShape = null;

    for (const shape of this.collisionShapes) {
      if (shape === excludeShape) continue;
      if (px < shape.bounds.minX || px > shape.bounds.maxX) continue;

      for (const edge of shape.edges) {
        const { a, b } = edge;
        const dx = b.x - a.x;
        if (Math.abs(dx) < 0.001) continue;

        if ((a.x > px && b.x > px) || (a.x < px && b.x < px)) continue;

        const t = (px - a.x) / dx;
        if (t < 0 || t > 1) continue;
        const ey = a.y + t * (b.y - a.y);

        if (ey < minY || ey > maxY) continue;
        if (ey >= bestY) continue;

        if (shape.oneWay || pointInPolygon(px, ey + 0.5, shape.vertices)) {
          bestY = ey;
          bestShape = shape;
        }
      }
    }

    return bestShape ? { y: bestY, shape: bestShape } : null;
  }

  // ── Collision resolution (parameterized for any entity) ──

  resolveEntityCollisions(entity, dtScale = 1) {
    const prevFeetY = entity.y + entity.h;
    const wasOnGround = entity.onGround;

    // ── Horizontal pass ──
    entity.x += entity.vx * dtScale;

    const BOUNDARY_PAD = 15;
    const worldRight = this.mapCols * TILE * MAP_SCALE;
    if (entity.x < BOUNDARY_PAD) { entity.x = BOUNDARY_PAD; entity.vx = 0; }
    else if (entity.x + entity.w > worldRight - BOUNDARY_PAD) { entity.x = worldRight - entity.w - BOUNDARY_PAD; entity.vx = 0; }

    // Wall collision (SAT)
    for (const shape of this.collisionShapes) {
      if (shape.oneWay) continue;
      if (!aabbOverlapsBounds(entity.x, entity.y, entity.w, entity.h, shape.bounds)) continue;

      const av = aabbVertices(entity.x, entity.y, entity.w, entity.h);
      const result = satOverlap(av, shape.vertices);
      if (!result) continue;

      if (Math.abs(result.axis.x) > Math.abs(result.axis.y)) {
        entity.x += result.axis.x * result.overlap;
        entity.vx = 0;
      }
    }

    // ── Vertical pass ──
    entity.y += entity.vy * dtScale;

    entity.onGround = false;

    if (entity.vy >= 0) {
      const feetY = entity.y + entity.h;
      const upwardSearch = wasOnGround ? MAX_GROUNDED_STEP_UP : 2;
      const downwardSearch = wasOnGround ? 16 : 2;
      const searchMinY = Math.min(prevFeetY, feetY) - upwardSearch;
      const searchMaxY = Math.max(prevFeetY, feetY) + downwardSearch;

      const leftX = entity.x + 2;
      const rightX = entity.x + entity.w - 2;
      const left = this.findGroundY(leftX, searchMinY, searchMaxY, entity.droppedShape);
      const right = this.findGroundY(rightX, searchMinY, searchMaxY, entity.droppedShape);

      let ground = null;
      if (left && right) ground = left.y < right.y ? left : right;
      else ground = left || right;

      if (ground) {
        const stepUpPx = prevFeetY - ground.y;
        const shouldSnap = wasOnGround
          ? stepUpPx <= MAX_GROUNDED_STEP_UP
          : feetY >= ground.y - 1;
        if (shouldSnap) {
          if (ground.shape.oneWay) {
            const tolerance = entity.sliding ? 12 : 4;
            if (prevFeetY <= ground.y + tolerance && ground.shape !== entity.droppedShape) {
              entity.y = ground.y - entity.h;
              entity.vy = 0;
              entity.onGround = true;
              entity.droppedShape = null;
            }
          } else {
            entity.y = ground.y - entity.h;
            entity.vy = 0;
            entity.onGround = true;
            entity.droppedShape = null;
          }
        }
      }
    }
  }

  // ── Update ──

  readInputKeys() {
    return {
      left: this.cursors.left.isDown,
      right: this.cursors.right.isDown,
      up: this.cursors.up.isDown,
      down: this.cursors.down.isDown,
      space: this.cursors.space.isDown,
      f: this.keyF.isDown,
      d: this.keyD.isDown,
      a: this.keyA.isDown,
      s: this.keyS.isDown,
      h: this.keyH.isDown,
      r: this.keyR.isDown,
      t: this.keyT.isDown,
    };
  }

  getGameplayKeys(keys) {
    if (!this.chatOpen && !this.bulletinBoardOpen) return keys;
    return {
      left: false,
      right: false,
      up: false,
      down: false,
      space: false,
      f: false,
      d: false,
      a: false,
      s: false,
      h: false,
      r: false,
      t: false,
    };
  }

  runSimulationStep(time, dt, dtScale, keys) {
    const p = this.playerEntity;
    if (!p) return;

    this.frameScale = dtScale;
    this.lastPlayerAttackHitbox = null;
    if (this.portalCooldownMs > 0) this.portalCooldownMs = Math.max(0, this.portalCooldownMs - dt);

    const uiHandled = applyUIStateTransitions(this, { dt, keys, player: p });
    let movementHandled = false;
    if (!uiHandled) {
      movementHandled = applyPlayerMovement(this, {
        dt,
        dtScale,
        keys,
        aabbOverlap,
        enableDebugHitboxToggle: ENABLE_RUNTIME_HITBOX_TOGGLE,
      });
    }
    if (!uiHandled && !movementHandled) {
      const { playerAttackHitbox } = applyCombatActions(this, { aabbOverlap });
      this.lastPlayerAttackHitbox = playerAttackHitbox;
      applyCombatWorldUpdates(this, { dt, dtScale, aabbOverlap });
      applyEnemyCombat(this, { aabbOverlap });
    }
    this.sendLocalPlayerInput(time, dt);
  }

  getRenderPlayerPosition() {
    const p = this.playerEntity;
    if (!p) return { x: 0, y: 0 };
    const prevX = Number.isFinite(this.renderPrevPlayerX) ? this.renderPrevPlayerX : p.x;
    const prevY = Number.isFinite(this.renderPrevPlayerY) ? this.renderPrevPlayerY : p.y;
    const currX = Number.isFinite(this.renderCurrPlayerX) ? this.renderCurrPlayerX : p.x;
    const currY = Number.isFinite(this.renderCurrPlayerY) ? this.renderCurrPlayerY : p.y;
    const alpha = clamp(Number.isFinite(this.renderLerpAlpha) ? this.renderLerpAlpha : 1, 0, 1);
    return {
      x: prevX + (currX - prevX) * alpha,
      y: prevY + (currY - prevY) * alpha,
    };
  }

  update(time, delta) {
    const normalizedDelta = normalizeDeltaMs(delta);
    const frameDt = clamp(normalizedDelta, 1, MAX_FRAME_DT_MS);
    const rawScale = clamp(frameDt / BASE_FRAME_MS, MIN_PHYSICS_FRAME_SCALE, MAX_PHYSICS_FRAME_SCALE);
    const renderDtScale = Number.isFinite(rawScale) ? rawScale : 1;
    this.renderFrameScale = renderDtScale;

    if (DEBUG_DELTA_TELEMETRY && (time - this.lastDeltaTelemetryAt) >= 1000) {
      this.lastDeltaTelemetryAt = time;
      const fps = frameDt > 0 ? (1000 / frameDt).toFixed(1) : "n/a";
      console.debug("[delta]", {
        rawDelta: delta,
        normalizedDeltaMs: normalizedDelta,
        frameDtMs: frameDt,
        renderDtScale,
        simSteps: this.lastSimStepCount,
        fps,
      });
    }

    if (this.bulletinBoardOpen && Phaser.Input.Keyboard.JustDown(this.keyEsc)) {
      this.closeBulletinBoard();
    } else if (this.chatOpen && Phaser.Input.Keyboard.JustDown(this.keyEsc)) {
      this.closeChat();
    }
    if (Phaser.Input.Keyboard.JustDown(this.keyF3)) {
      this.ui?.toggleNetworkDebug?.();
    }
    if (!this.chatOpen && !this.bulletinBoardOpen && Phaser.Input.Keyboard.JustDown(this.keyEnter)) {
      this.openChat();
    } else if (this.chatOpen && Phaser.Input.Keyboard.JustDown(this.keyEnter)) {
      this.submitChat();
    }

    const keys = this.readInputKeys();
    const gameplayKeys = this.getGameplayKeys(keys);

    this.lastPlayerAttackHitbox = null;
    this.simAccumulatorMs = Math.min(MAX_ACCUMULATED_MS, this.simAccumulatorMs + frameDt);
    const p = this.playerEntity;
    if (p && !Number.isFinite(this.renderCurrPlayerX)) {
      this.renderPrevPlayerX = p.x;
      this.renderPrevPlayerY = p.y;
      this.renderCurrPlayerX = p.x;
      this.renderCurrPlayerY = p.y;
    }
    let simSteps = 0;
    while (this.simAccumulatorMs >= SIM_STEP_MS && simSteps < MAX_CATCHUP_STEPS) {
      this.renderPrevPlayerX = this.renderCurrPlayerX;
      this.renderPrevPlayerY = this.renderCurrPlayerY;
      this.runSimulationStep(time, SIM_STEP_MS, 1, gameplayKeys);
      if (p) {
        this.renderCurrPlayerX = p.x;
        this.renderCurrPlayerY = p.y;
        if (Math.hypot(this.renderCurrPlayerX - this.renderPrevPlayerX, this.renderCurrPlayerY - this.renderPrevPlayerY) > 96) {
          this.renderPrevPlayerX = this.renderCurrPlayerX;
          this.renderPrevPlayerY = this.renderCurrPlayerY;
        }
      }
      this.simAccumulatorMs -= SIM_STEP_MS;
      simSteps += 1;
    }
    this.lastSimStepCount = simSteps;
    this.simLastTimeMs = time;
    this.renderLerpAlpha = clamp(this.simAccumulatorMs / SIM_STEP_MS, 0, 1);

    this.refreshChatHud();
    applyFramePostUpdate(this, {
      time,
      dt: frameDt,
      dtScale: renderDtScale,
      playerAttackHitbox: this.lastPlayerAttackHitbox,
    });
    this.updateChatBubbles(time);
  }

  // ── Camera ──

  updateCam() {
    const p = this.playerEntity;
    const renderPos = this.getRenderPlayerPosition();
    const cam = this.cameras.main;
    const dtScale = this.renderFrameScale || 1;
    const followLerp = 1 - Math.pow(1 - 0.1, dtScale);
    const viewW = cam.width;
    const viewH = cam.height;
    const targetX = renderPos.x + p.w / 2 - viewW / 2;
    const maxX = Math.max(0, this.mapCols * TILE * MAP_SCALE - viewW);
    const clampedX = Math.max(0, Math.min(targetX, maxX));
    cam.scrollX += (clampedX - cam.scrollX) * followLerp;

    const targetY = renderPos.y + p.h / 2 - viewH / 2;
    const maxY = Math.max(0, this.mapRows * TILE * MAP_SCALE - viewH);
    const clampedY = Math.max(0, Math.min(targetY, maxY));
    cam.scrollY += (clampedY - cam.scrollY) * followLerp;

    this.bg1.tilePositionX = cam.scrollX * 0.02;
    this.bg2.tilePositionX = cam.scrollX * 0.06;
  }

  // ── Animation State ──

  resolveAnimationState(dt) {
    const p = this.playerEntity;
    const a = this.anim;
    let nextAnim = "Idle";

    if (p.dying || p.dead) {
      nextAnim = "Dying";
    } else if (p.hurting) {
      nextAnim = "Hurt";
    } else if (p.attacking) {
      const attackAnims = [
        "Slashing", "Run Slashing", "Slashing in The Air",
        "Throwing", "Run Throwing", "Throwing in The Air", "Kicking"
      ];
      if (attackAnims.includes(a.current)) {
        nextAnim = a.current;
      } else if (p.attackType === "slash") {
        if (!p.onGround) nextAnim = "Slashing in The Air";
        else nextAnim = "Slashing";
      } else if (p.attackType === "throw") {
        if (!p.onGround) nextAnim = "Throwing in The Air";
        else if (Math.abs(p.vx) > 1.5) nextAnim = "Run Throwing";
        else nextAnim = "Throwing";
      } else if (p.attackType === "kick") {
        nextAnim = "Kicking";
      }
    } else if (p.onLadder) {
      nextAnim = "Idle";
    } else if (!p.onGround) {
      nextAnim = "Jump Loop";
    } else if (a.current === "Jump Loop") {
      if (p.sliding) nextAnim = "Sliding";
      else if (Math.abs(p.vx) > 1.5) nextAnim = "Running";
      else if (Math.abs(p.vx) > 0.3) nextAnim = "Walking";
      else nextAnim = "Idle";
    } else if (p.sliding) {
      nextAnim = "Sliding";
    } else if (p.ducking) {
      nextAnim = "Idle";
    } else if (Math.abs(p.vx) > 1.5) {
      nextAnim = "Running";
    } else if (Math.abs(p.vx) > 0.3) {
      nextAnim = "Walking";
    } else {
      a.blinkTimer += dt;
      if (a.blinkTimer > a.blinkInterval) {
        a.blinkTimer = 0;
        nextAnim = "Idle Blinking";
      } else if (a.current === "Idle Blinking") {
        const blinkKey = this.resolvePlayableAnimKey(this.localCharacterId, "Idle Blinking", "Idle");
        const idleKey = this.resolvePlayableAnimKey(this.localCharacterId, "Idle", "Idle");
        if (!this.playerSprite.anims || !this.playerSprite.anims.currentAnim) {
          nextAnim = "Idle";
        } else if (blinkKey && idleKey && blinkKey !== idleKey &&
                   this.playerSprite.anims.currentAnim.key === blinkKey &&
                   this.playerSprite.anims.isPlaying) {
          nextAnim = "Idle Blinking";
        } else {
          nextAnim = "Idle";
        }
      } else {
        nextAnim = "Idle";
      }
    }

    if (nextAnim !== a.current) {
      a.prev = a.current;
      a.current = nextAnim;
      const phaserKey = this.resolvePlayableAnimKey(this.localCharacterId, nextAnim, "Idle");
      if (phaserKey) this.playerSprite.play(phaserKey);
    }
  }

  // ── Sync sprite position to player entity ──

  syncSprite() {
    const p = this.playerEntity;
    const renderPos = this.getRenderPlayerPosition();
    this.playerSprite.x = renderPos.x + p.w / 2 - this.getCharacterCenterOffsetX(this.localCharacterId);
    this.playerSprite.y = renderPos.y + p.h + this.getCharacterFootOffset(this.localCharacterId);
    this.playerSprite.setDepth(1100 + this.playerSprite.y);
    this.playerSprite.setFlipX(p.facing === -1);
    if (this.localPlayerNameText) {
      const localFootOffset = this.getCharacterFootOffset(this.localCharacterId);
      this.localPlayerNameText.x = this.playerSprite.x;
      this.localPlayerNameText.y = this.playerSprite.y - localFootOffset + this.remotePlayerLabelOffsetY;
      this.localPlayerNameText.setDepth(1101 + this.playerSprite.y);
    }

    if (p.dying) {
      this.playerSprite.setAlpha(Math.max(0, 1 - p.dieTime / 500));
    } else {
      this.playerSprite.setAlpha(1);
      this.playerSprite.clearTint();
    }
  }

  sanitizeCharacterId(raw) {
    return getCharacterDef(typeof raw === "string" ? raw : "").id;
  }

  getSelectedCharacterId() {
    return readSelectedCharacter(this.sanitizeCharacterId.bind(this), DEFAULT_CHARACTER_ID);
  }

  getAnimKeyForState(characterId, state) {
    return composeAnimKey(this.sanitizeCharacterId(characterId), state);
  }

  resolvePlayableAnimKey(characterId, state, fallbackState = "Idle") {
    const primary = this.getAnimKeyForState(characterId, state);
    if (primary && this.anims.exists(primary)) return primary;
    const fallback = this.getAnimKeyForState(characterId, fallbackState);
    if (fallback && this.anims.exists(fallback)) return fallback;
    const defaultPrimary = composeAnimKey(DEFAULT_CHARACTER_ID, state);
    if (defaultPrimary && this.anims.exists(defaultPrimary)) return defaultPrimary;
    const defaultIdle = composeAnimKey(DEFAULT_CHARACTER_ID, "Idle");
    return this.anims.exists(defaultIdle) ? defaultIdle : null;
  }

  getAnimAtlasForState(characterId, state) {
    const character = getCharacterDef(this.sanitizeCharacterId(characterId));
    const spec = BASE_ANIM_SPECS.find((s) => s.state === state) || BASE_ANIM_SPECS[0];
    return composeAtlasKey(character.prefix, spec.suffix);
  }

  resolvePlayableAtlasForState(characterId, state, fallbackState = "Idle") {
    const primary = this.getAnimAtlasForState(characterId, state);
    if (primary && this.textures.exists(primary)) return primary;
    const fallback = this.getAnimAtlasForState(characterId, fallbackState);
    if (fallback && this.textures.exists(fallback)) return fallback;
    const defaultIdle = this.getAnimAtlasForState(DEFAULT_CHARACTER_ID, "Idle");
    return this.textures.exists(defaultIdle) ? defaultIdle : primary;
  }

  calibrateCharacterAnchors() {
    this.characterFootOffsets.clear();
    this.characterCenterOffsets.clear();
    this.characterMeasuredOrigins.clear();
    for (const character of CHARACTER_OPTIONS) {
      const atlas = this.getAnimAtlasForState(character.id, "Idle");
      const frameName = `${atlas}_00`;
      const measured = this.measureCharacterAnchorCalibration(atlas, frameName, character.originX, character.originY);
      if (!measured) {
        this.characterFootOffsets.set(character.id, 0);
        this.characterCenterOffsets.set(character.id, 0);
        continue;
      }
      this.characterFootOffsets.set(character.id, measured.footOffsetY);
      this.characterCenterOffsets.set(character.id, measured.centerOffsetX);
      this.characterMeasuredOrigins.set(character.id, {
        x: measured.measuredOriginX,
        y: measured.measuredOriginY,
      });
    }
  }

  measureCharacterAnchorCalibration(atlasKey, frameName, fallbackOriginX, fallbackOriginY) {
    const frame = this.textures.getFrame(atlasKey, frameName);
    if (!frame) return null;

    const sourceImage = frame.source?.image || this.textures.get(atlasKey)?.getSourceImage();
    if (!sourceImage) return null;

    const sx = Math.floor(Number.isFinite(frame.cutX) ? frame.cutX : frame.x);
    const sy = Math.floor(Number.isFinite(frame.cutY) ? frame.cutY : frame.y);
    const sw = Math.floor(Number.isFinite(frame.cutWidth) ? frame.cutWidth : frame.width);
    const sh = Math.floor(Number.isFinite(frame.cutHeight) ? frame.cutHeight : frame.height);
    if (!Number.isFinite(sx) || !Number.isFinite(sy) || !Number.isFinite(sw) || !Number.isFinite(sh) || sw <= 0 || sh <= 0) return null;

    if (!this.footOffsetCanvas) {
      this.footOffsetCanvas = document.createElement("canvas");
      this.footOffsetCtx = this.footOffsetCanvas.getContext("2d", { willReadFrequently: true });
    }
    const canvas = this.footOffsetCanvas;
    const ctx = this.footOffsetCtx;
    if (!canvas || !ctx) return null;

    canvas.width = sw;
    canvas.height = sh;
    ctx.clearRect(0, 0, sw, sh);
    ctx.drawImage(sourceImage, sx, sy, sw, sh, 0, 0, sw, sh);

    let data;
    try {
      data = ctx.getImageData(0, 0, sw, sh).data;
    } catch {
      return null;
    }

    let maxOpaqueY = -1;
    let minOpaqueX = sw;
    let maxOpaqueX = -1;
    const alphaThreshold = 8;
    for (let y = 0; y < sh; y++) {
      const rowStart = y * sw * 4;
      for (let x = 0; x < sw; x++) {
        if (data[rowStart + x * 4 + 3] > alphaThreshold) {
          minOpaqueX = Math.min(minOpaqueX, x);
          maxOpaqueX = Math.max(maxOpaqueX, x);
          maxOpaqueY = Math.max(maxOpaqueY, y);
        }
      }
    }
    if (maxOpaqueY < 0) return null;

    const frameCenterX = (sw - 1) / 2;
    const opaqueCenterX = (minOpaqueX + maxOpaqueX) / 2;
    const centerOffsetX = clamp(opaqueCenterX - frameCenterX, -32, 32);
    const originY = Number.isFinite(fallbackOriginY) ? fallbackOriginY : 0.5;
    const rawOffsetY = originY * sh - (maxOpaqueY + 1);
    const footOffsetY = Math.round(clamp(rawOffsetY, -24, 24));
    const measuredOriginX = clamp(
      Number.isFinite(sw) && sw > 0 ? opaqueCenterX / sw : (Number.isFinite(fallbackOriginX) ? fallbackOriginX : 0.5),
      0,
      1,
    );
    const measuredOriginY = clamp(
      Number.isFinite(sh) && sh > 0 ? (maxOpaqueY + 1) / sh : (Number.isFinite(fallbackOriginY) ? fallbackOriginY : 0.5),
      0,
      1,
    );
    return {
      footOffsetY,
      centerOffsetX: Math.round(centerOffsetX * 100) / 100,
      measuredOriginX,
      measuredOriginY,
    };
  }

  getCharacterFootOffset(characterId) {
    const character = getCharacterDef(this.sanitizeCharacterId(characterId));
    if (this.characterFootOffsets.has(character.id)) return this.characterFootOffsets.get(character.id);
    return 0;
  }

  getCharacterCenterOffsetX(characterId) {
    const character = getCharacterDef(this.sanitizeCharacterId(characterId));
    if (this.characterCenterOffsets.has(character.id)) return this.characterCenterOffsets.get(character.id);
    return 0;
  }

  applySpriteOriginForCharacter(sprite, characterId) {
    if (!sprite) return;
    const character = getCharacterDef(this.sanitizeCharacterId(characterId));
    const measured = this.characterMeasuredOrigins.get(character.id);
    if (measured && Number.isFinite(measured.x)) {
      // Keep runtime X calibration for horizontal alignment, but preserve
      // catalog/foot-offset Y anchoring which is more stable across effects.
      sprite.setOrigin(measured.x, character.originY);
      return;
    }
    sprite.setOrigin(character.originX, character.originY);
  }

  updateNetworkStartupPhase() {
    let phase = "";
    if (this.netStatus !== "connected") {
      phase = this.netStatus === "connecting" ? "Connecting..." : "";
    } else if (!this.netStartupPlayersReady) {
      phase = "Syncing players...";
    } else if (!this.netStartupScoreboardReady) {
      phase = "Syncing scoreboard...";
    }
    this.netStartupPhaseLabel = phase;
    if (this.netStatusText) {
      const base = `NET: ${this.netStatus}`;
      this.netStatusText.setText(phase ? `${base} | ${phase}` : base);
    }
  }

  initNetworking() {
    if (!NETWORK_ENABLED || typeof WebSocket === "undefined") return;
    if (this.netClient) return;

    const resolvedWebSocketUrl = resolveWebSocketUrl();
    this.netWsUrl = resolvedWebSocketUrl;
    this.scoreboardApiUrl = resolveScoreboardApiUrlFromWs(this.netWsUrl);
    this.netSessionId = readSessionId();
    this.netStartupPlayersReady = false;
    this.netStartupScoreboardReady = false;
    this.updateNetworkStartupPhase();
    this.ui?.setNetworkDebug?.({
      wsUrl: this.netWsUrl,
      playerId: this.netPlayerId,
      sessionId: this.netSessionId,
      status: this.netStatus,
      visible: true,
      tickRate: this.netServerTickRate,
      rttMs: this.netClient?.lastRttMs,
      remoteMotion: this.netRemoteMotionStats,
      inputSend: this.netInputSendStats,
    });
    this.netClient = createNetworkClient({
      url: resolvedWebSocketUrl,
      mapId: this.mapId,
      name: readPlayerName(),
      characterId: this.getSelectedCharacterId(),
      getSessionId: () => this.netSessionId,
      onStatus: (status) => {
        this.netStatus = status;
        if (status !== "connected") {
          this.netStartupPlayersReady = false;
          this.netStartupScoreboardReady = false;
        }
        this.updateNetworkStartupPhase();
        this.ui?.setNetworkDebug?.({
          wsUrl: this.netWsUrl,
          playerId: this.netPlayerId,
          sessionId: this.netSessionId,
          status,
          tickRate: this.netServerTickRate,
          rttMs: this.netClient?.lastRttMs,
          remoteMotion: this.netRemoteMotionStats,
          inputSend: this.netInputSendStats,
        });
      },
      onWelcome: (msg) => {
        this.netPlayerId = msg.playerId || null;
        if (isValidSessionId(msg?.sessionId)) {
          this.netSessionId = String(msg.sessionId).trim().toLowerCase();
        }
        if (Number.isFinite(Number(msg?.tickRate))) {
          this.netServerTickRate = Math.max(1, Math.floor(Number(msg.tickRate)));
        }
        this.ui?.setNetworkDebug?.({
          wsUrl: this.netWsUrl,
          playerId: this.netPlayerId,
          sessionId: this.netSessionId,
          status: this.netStatus,
          tickRate: this.netServerTickRate,
          rttMs: this.netClient?.lastRttMs,
          remoteMotion: this.netRemoteMotionStats,
          inputSend: this.netInputSendStats,
        });
        if (this.netSessionId) {
          writeSessionId(this.netSessionId);
        }
        if (Array.isArray(msg?.players) && msg.players.length > 0) {
          applyNetworkStateSync(this, {
            players: msg.players,
            serverTime: msg.serverTime,
            tickRate: msg.tickRate,
          });
          this.updateScoreboardFromState(msg.players);
        }
        if (msg?.scoreboard && typeof msg.scoreboard === "object") {
          this.applyWelcomeScoreboardSnapshot(msg.scoreboard);
        }
        if (msg?.bulletinBoard && typeof msg.bulletinBoard === "object") {
          this.applyBulletinBoardSnapshot(msg.bulletinBoard);
        }
        this.netStartupPlayersReady = Array.isArray(msg?.players);
        this.updateNetworkStartupPhase();
        this.fetchServerScoreboard(true);
      },
      onState: (msg) => {
        applyNetworkStateSync(this, msg);
        this.updateScoreboardFromState(msg?.players);
      },
      onPlayerJoined: (msg) => {
        const joined = msg?.player;
        if (!joined || !joined.id) return;
        if (String(joined.id) === String(this.netPlayerId || "")) return;
        const existing = this.remotePlayers.get(joined.id);
        if (existing) {
          existing.name = joined.name || existing.name || "Player";
          if (existing.label) existing.label.setText(existing.name);
        } else {
          const remote = this.createRemotePlayer(joined);
          this.remotePlayers.set(joined.id, remote);
        }
        const merged = mergeScoreboardRecord(
          this.scoreboardKnownPlayers.get(String(joined.id)),
          normalizeAnyScoreboardRecord(joined) || {
            id: String(joined.id),
            name: sanitizeScoreboardName(joined.name),
            playerKills: 0,
            playerDeaths: 0,
            enemyKills: 0,
            enemyDeaths: 0,
            lastSeenAt: Date.now(),
          }
        );
        this.scoreboardKnownPlayers.set(String(joined.id), merged);
        saveScoreboardOfflineHistory(this.scoreboardKnownPlayers);
        const joinedId = String(joined.id);
        if (!this.scoreboardRows.some((entry) => String(entry?.id) === joinedId)) {
          const liveRows = [...this.scoreboardRows, merged];
          const normalizedLive = applyScoreboardDisambiguation(liveRows);
          this.scoreboardDisplayNameById = new Map(normalizedLive.map((entry) => [String(entry.id), entry.displayName || entry.name]));
          this.scoreboardRows = normalizedLive
            .sort((a, b) => {
              if (b.playerKills !== a.playerKills) return b.playerKills - a.playerKills;
              if (a.playerDeaths !== b.playerDeaths) return a.playerDeaths - b.playerDeaths;
              if (b.enemyKills !== a.enemyKills) return b.enemyKills - a.enemyKills;
              return (a.name || "").localeCompare(b.name || "");
            })
            .slice(0, SCOREBOARD_MAX_ROWS);
          const liveSet = new Set(this.scoreboardRows.map((entry) => String(entry.id)));
          this.scoreboardOfflineRows = Array.from(this.scoreboardKnownPlayers.values())
            .filter((entry) => !liveSet.has(String(entry.id)))
            .sort((a, b) => {
              if (b.playerKills !== a.playerKills) return b.playerKills - a.playerKills;
              if (a.playerDeaths !== b.playerDeaths) return a.playerDeaths - b.playerDeaths;
              if (b.enemyKills !== a.enemyKills) return b.enemyKills - a.enemyKills;
              return (a.name || "").localeCompare(b.name || "");
            });
          this.ui?.setScoreboard?.({
            players: this.scoreboardRows,
            offlinePlayers: this.scoreboardOfflineRows,
            localPlayerId: this.netPlayerId,
          });
        }
        this.netStartupPlayersReady = true;
        this.updateNetworkStartupPhase();
      },
      onPlayerLeft: (msg) => this.removeRemotePlayer(msg.playerId),
      onProjectileSpawn: (msg) => handleNetworkProjectileSpawn(this, msg),
      onProjectileHit: (msg) => handleNetworkProjectileHit(this, msg),
      onCombatHit: (msg) => handleNetworkCombatHit(this, msg),
      onCombatSwing: (msg) => handleNetworkCombatSwing(this, msg),
      onChatMessage: (msg) => {
        const isSystem = !!msg?.isSystem || (typeof msg?.channel === "string" && msg.channel === "system");
        const senderName = isSystem ? "SYSTEM" : (typeof msg?.name === "string" && msg.name.trim() ? msg.name.trim() : "Player");
        const senderId = msg?.playerId != null ? String(msg.playerId) : null;
        const localPlayerId = this.netPlayerId != null ? String(this.netPlayerId) : null;
        const isSelf = senderId !== null && localPlayerId !== null && senderId === localPlayerId;
        const displayName = this.getDisplayNameForChatPlayer(senderId, senderName, isSelf);
        const text = typeof msg?.text === "string" ? msg.text : "";
        if (!text) return;
        const bubblePlayerId = isSystem ? null : (isSelf && localPlayerId ? localPlayerId : senderId);
        if (bubblePlayerId) {
          this.setChatBubbleMessage(bubblePlayerId, text, msg?.serverTime);
        }
        this.chatHistory.push({
          playerId: senderId,
          name: isSelf ? "You" : displayName,
          text,
          isSelf,
          isSystem,
        });
        if (this.chatHistory.length > this.chatMaxHistory) {
          this.chatHistory = this.chatHistory.slice(-this.chatMaxHistory);
        }
        this.refreshChatHud();
      },
      onBulletinBoardSnapshot: (msg) => {
        this.applyBulletinBoardSnapshot(msg);
      },
    });
    this.netClient.connect();
  }

  teardownNetworking() {
    if (this.netClient) {
      stopNetworkClient(this.netClient);
      this.netClient = null;
    }
    this.chatOpen = false;
    this.chatInputText = "";
    this.closeBulletinBoard();
    this.bulletinBoardMessages = [];
    if (this.chatInputHandler) {
      this.input.keyboard.off("keydown", this.chatInputHandler);
      this.chatInputHandler = null;
    }
    this.refreshChatHud();
    this.resetScoreboard();
    for (const playerId of [...this.remotePlayers.keys()]) {
      this.removeRemotePlayer(playerId);
    }
    this.clearAllChatBubbles();
    this.clearQuestBoardHints();
    this.remotePlayers.clear();
    this.netProjectilesById.clear();
    this.netPendingProjectilesByToken.clear();
    this.scoreboardApiUrl = null;
    this.scoreboardServerFetchInFlight = false;
    this.scoreboardServerLoaded = false;
    this.scoreboardServerResetVersion = 0;
    this.scoreboardLastFetchedAt = 0;
    this.netWsUrl = null;
    this.netPlayerId = null;
    this.netServerTimeOffsetMs = null;
    this.netServerTickRate = 30;
    this.netRemoteMotionStats = null;
    this.netRemoteSnapEventsMs = [];
    this.netLastInputSentAt = 0;
    this.netInputSeq = 0;
    this.netInputAccumulatorMs = 0;
    this.netLastInputSchedulerAtMs = 0;
    this.netInputSendIntervalsMs = [];
    this.netInputSendStats = null;
    this.netStartupPlayersReady = false;
    this.netStartupScoreboardReady = false;
    this.netStartupPhaseLabel = "";
    this.updateNetworkStartupPhase();
    this.ui?.hideNetworkDebug?.();
  }

  createRemotePlayer(player) {
    return createRemotePlayerForScene(this, player, {
      feetUiOffsetY: this.remotePlayerLabelOffsetY,
    });
  }

  removeRemotePlayer(playerId) {
    this.destroyChatBubble(playerId);
    removeRemotePlayerForScene(this, playerId);
  }

  updateRemotePlayers(dtScale) {
    return updateRemotePlayersForScene(this, dtScale);
  }

  sendLocalPlayerInput(time, frameDtMs = 0) {
    sendLocalPlayerInputSync(this, time, frameDtMs);
  }

  updateNetworking(time, dtScale) {
    updateNetworkRuntime(this, time, dtScale);
  }
}
